/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useState,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import {
  PlusOutlined,
  CalendarOutlined,
  AimOutlined,
  ProjectOutlined,
  FileTextOutlined,
  DashboardOutlined,
  PieChartOutlined,
  UserOutlined,
  TeamOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  DownCircleOutlined,
  SendOutlined,
  PaperClipOutlined,
  InfoCircleOutlined,
  WarningFilled,
} from '@ant-design/icons';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Progress,
  Tooltip,
  FormInstance,
  message,
  Checkbox,
  Popover,
  Badge,
  Space,
} from 'antd';
import dayjs from 'dayjs';
import { employeeSDK } from '@/types/sdk/model';
import { SelectList, SelectListItem } from '@/types/action-plan/DuAnDetailView';
import { DuAnCongViecChiTietView, DuAnCongViecChiTieuItem, DuAnCongViecTree, CongViecDuAnCheck } from '@/types/action-plan/DuAnView';
import ActionPlanDateTimePicker from '../../../Modals/ModalFormCreate/ActionPlanDateTimePicker';
import EmployeeAvatarSelect from '@/components/common/ActionPlan/Common/EmployeeAvatarSelect';
import { EmployeeAvatar, type EmployeeInfo } from '@/components/common/ActionPlan/Common/EmployeeAvatar';
import { FileAndLinkUploadMenu, FilePreviewModal, extractFileNameFromUrl, mapFinderSelectionToFileUrls } from '@/components/common/FileAndLinkUpload';
import { CONSTANTS } from '@/utils/constants';
import { congviecduanApi } from '@/services/action-plan/congviecduan';
import {
  DEFAULT_CHILD_COLUMNS,
  createNewChildColumn,
} from '../../../defaultModels';
import {
  EditCongViecsRequest,
} from '@/types/action-plan/ActionPlan/EditCongViecsRequest';
import { DuAnCongViecDTO } from '@/types/action-plan/ActionPlan/DuAnCongViecDTO';
import { DuAnCongViecDatetimePicker } from '../../CommonComponent/DuAnCongViecDatetimePicker';
import { useLocalFilter } from '@/hooks/useOrganizationIndexedDBFilter';
import { useAuthStore } from '@/store/useAuthStore';
import { createUnitHelpers } from '@/components/MeasureIndicators/helpers/unitHelpers';
import ChiaChiTieuModal from '../../CommonComponent/ChiaChiTieuModal';
import { mergeListChiTieuItems } from '../../CommonComponent/chiaChiTieuModal.helpers';
import { normalizeListChiTieuNgayForApi, toActionPlanCalendarDate } from '@/utils/dateOnlyForApi';
import {
  DiscussComponent,
  DiscussItem,
  resolveDiscussAuthorLabel,
} from '../../DiscussComponent';
import { useSignalR } from '@/hooks/useSignalR';
import CommonJs from '@/utils/commonJs';
import FinderJs from '@/utils/finderJS';
import {
  applyUploadProgress,
  applyUploadResults,
  createUploadItems,
  failUnresolvedUploads,
  hasPendingUploads,
  removeUploadItem,
  type ClientUploadItem,
} from '@/components/common/ActionPlanV2/core/task';
import { validateAndConvertDate } from '@/utils/datetime';
import {
  convertChildsColumnsToDuAnCongViecTree,
  joinNguoiPhuTrachFormValue,
  firstPhuTrachIdForChildDefault,
} from '../../../Modals/ModalFormCreate/helpers';
import { DuAnCongViecNganSachDTO } from '@/types/action-plan/ActionPlan/DuAnCongViecNganSachDTO';
import { baocaoApi } from '@/services/action-plan/baocao';
import BudgetIcon from '@/assets/images/BO/BO_Opportunity_32x32.png';
import FormattedDecimalInput from '@/components/common/FormattedDecimalInput';
import { ViTriSiblingDragList, ViTriCheckItem } from './ViTriSiblingDragList';
import CustomTooltip from '@/components/common/Tooltip';
import CKEditorWrapperActionPlan from '@/components/Editor/CKEditorWrapperActionPlan';

const normalizeCheckItem = (raw: any): ViTriCheckItem => ({
  id: String(raw?.id ?? raw?.ID ?? ''),
  parentID: String(raw?.parentID ?? raw?.ParentID ?? ''),
  nganSach: Number(raw?.nganSach ?? raw?.NganSach ?? 0),
  tiTrong: (raw?.tiTrong ?? raw?.TiTrong ?? null) as number | null,
  tiTrongCal: (raw?.tiTrongCal ?? raw?.TiTrongCal ?? null) as number | null,
  ngayBatDau: (raw?.ngayBatDau ?? raw?.NgayBatDau ?? null) as Date | null,
  ngayKetThuc: (raw?.ngayKetThuc ?? raw?.NgayKetThuc ?? null) as Date | null,
  viTri: (raw?.viTri ?? raw?.ViTri ?? null) as number | null,
  hasChange: raw?.hasChange === true || raw?.HasChange === true,
  tenCongViec: raw?.tenCongViec ?? raw?.TenCongViec,
});

const NGANSACH_UNIT = CONSTANTS.NGANSACH_UNIT;
const CHECKLIST_SYNC_TIMEOUT = 500;
const ASSIGNEE_SELECT_BOX = 'cvda-detail-form-assignee-control';
/** false = modal căn trái, true = column bảng căn giữa */
const isColumn = false;

const FormRow: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`cvda-detail-form-row ${className}`.trim()}>{children}</div>
);

const FormLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="cvda-detail-form-label">{children}</span>
);

const FormField: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`cvda-detail-form-field ${className}`.trim()}>{children}</div>
);

const parseExcludeEmployeeIds = (raw: unknown): string[] => {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  return String(raw).split(',').map((s) => s.trim()).filter(Boolean);
};

type TargetSelectOption = { value: string; label: string; disabled?: boolean };

function buildTargetOptionsFromSelectList(
  selectListMucTieu: SelectList | SelectListItem[] | undefined
): TargetSelectOption[] {
  if (!selectListMucTieu) return [];
  if (Array.isArray(selectListMucTieu)) {
    return selectListMucTieu.map((item) => ({
      value: String(item.value),
      label: String(item.text ?? ''),
      disabled: Boolean(item.disabled),
    }));
  }
  if (selectListMucTieu.items && Array.isArray(selectListMucTieu.items)) {
    return selectListMucTieu.items.map((item) => ({
      value: String(item.value),
      label: String(item.text ?? ''),
      disabled: Boolean(item.disabled),
    }));
  }
  return [];
}

function pickBetterTargetLabel(prev: string, next: string, id: string): string {
  const a = (prev ?? '').trim();
  const b = (next ?? '').trim();
  if (b && b !== id) return b;
  if (a && a !== id) return a;
  return b || a || id;
}

function mergeTargetOptionLists(lists: TargetSelectOption[][]): TargetSelectOption[] {
  const map = new Map<string, TargetSelectOption>();
  for (const list of lists) {
    for (const o of list) {
      if (!o?.value) continue;
      const prev = map.get(o.value);
      const label = pickBetterTargetLabel(prev?.label ?? '', o.label ?? '', o.value);
      map.set(o.value, {
        value: o.value,
        label,
        disabled: Boolean(prev?.disabled || o.disabled),
      });
    }
  }
  return Array.from(map.values());
}
interface ColumnItem {
  id: number | string; // Có thể là number (từ backend) hoặc string (tempxxx cho item mới)
  projectName: string;
  person: string;
  dateTime: string;
  // Hoàn thành & trạng thái giống table cha
  completionCount?: number;
  completionPercentage?: number;
  originalData?: DuAnCongViecTree;
}


export interface FormInfomationProps {
  form?: FormInstance<any>;
  nhanViens?: employeeSDK[];
  selectListMucTieu?: SelectList | SelectListItem[];
  selectListDuAn?: SelectList | SelectListItem[];
  progress?: number;
  childs?: DuAnCongViecTree[];
  budgetPercentage?: number; // % ngân sách thực hiện (0-100)
  sumNganSachChild?: number; // Tổng ngân sách thực tế của các child
  isEdit?: boolean; // true = edit, false = create new
  data?: DuAnCongViecChiTietView;
  loading?: boolean;
  idDuAn?: string; // ID dự án để lưu
  onSave?: (payload?: { listChange?: DuAnCongViecTree[] }) => void; // Callback sau khi lưu thành công
  onCancel?: () => void; // Callback khi đóng
  parentCongViec?: any;
  dicNganSach?: Record<string, DuAnCongViecNganSachDTO>;
  /** Ẩn khối thảo luận (dùng tab Thảo luận riêng). */
  hideDiscussion?: boolean;
  /** Chỉ hiển thị khối thảo luận. */
  discussionOnly?: boolean;
}

// Sử dụng DEFAULT_CHILD_COLUMNS từ defaultModels
const initialColumns: ColumnItem[] = DEFAULT_CHILD_COLUMNS;
/** Tránh `childs = []` tạo reference mới mỗi render → loop useEffect([childs]) */
const EMPTY_CHILDS: DuAnCongViecTree[] = [];

function normalizeFormNvIds(raw: unknown): string[] {
  if (raw == null) return [];
  if (Array.isArray(raw))
    return raw.filter(Boolean).map((x) => String(x).trim()).filter(Boolean);
  return String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function normMentionSearch(s: string): string {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();
}

/** @mention: chỉ kích hoạt khi @ ở đầu chuỗi hoặc sau khoảng trắng; fragment không chứa space/@ thứ hai */
function detectReplyMentionAtCaret(
  val: string,
  caret: number
): null | { start: number; query: string } {
  const before = val.slice(0, caret);
  const lastAt = before.lastIndexOf('@');
  if (lastAt < 0) return null;
  if (lastAt > 0) {
    const ch = before[lastAt - 1];
    if (ch !== ' ' && ch !== '\n' && ch !== '\t') return null;
  }
  const frag = before.slice(lastAt + 1);
  if (!/^[^\s@]*$/.test(frag)) return null;
  return { start: lastAt, query: frag };
}

/** Nội dung chèn vào textarea: @ + nhãn (trùng cách hiển thị, không dùng id/login) */
function thaoLuanMentionInsertFromDisplayLabel(label: string): string {
  const t = String(label || '').trim().replace(/^@+/, '');
  if (!t) return '@';
  return `@${t}`;
}

/** Hiển thị trong dropdown/textarea: Tên - Bộ phận - Chức vụ */
function formatThaoLuanMentionDisplayLabel(
  ten: string,
  boPhan: string,
  chucVu: string
): string {
  return [ten, boPhan, chucVu]
    .map((s) => String(s || '').trim())
    .filter(Boolean)
    .join(' - ');
}

type ThaoLuanMentionOptionRow = {
  key: string;
  id: string;
  insert: string;
  label: string;
  search: string;
  payloadToken: string;
};

type ThaoLuanMentionSurface = 'discuss' | 'reply';

type ThaoLuanMentionState = {
  surface: ThaoLuanMentionSurface;
  start: number;
  query: string;
};

const THAO_LUAN_MENTION_LISTBOX_Z = 10050;

type PendingNewDiscussMark =
  | { kind: 'id'; id: string }
  | { kind: 'newestRoot' }
  | { kind: 'newestUnderParent'; parentId: string };

function extractSavedThaoLuanId(res: any): string | null {
  const d = res?.data;
  if (d == null) return null;
  if (typeof d === 'string' || typeof d === 'number') return String(d);
  const id = d.id ?? d.ID ?? d.thaoLuanId ?? d.ThaoLuanId;
  return id != null && String(id) !== '' ? String(id) : null;
}

function thaoLuanMentionStateFromCaret(
  val: string,
  caret: number,
  surface: ThaoLuanMentionSurface
): ThaoLuanMentionState | null {
  const det = detectReplyMentionAtCaret(val, caret);
  return det ? { surface, start: det.start, query: det.query } : null;
}

export interface FormInfomationRef {
  handleSave: () => Promise<void>;
  saving: boolean;
}

export const FormInfomation = forwardRef<FormInfomationRef, FormInfomationProps>(({
  form = undefined,
  selectListMucTieu,
  selectListDuAn,
  progress = 0,
  childs = EMPTY_CHILDS,
  isEdit = true,
  data,
  idDuAn,
  onSave,
  parentCongViec,
  hideDiscussion = false,
  discussionOnly = false,
}, ref) => {

  // Kiểm tra xem đang ở trang cong-viec-du-an hay không để disable mục tiêu select
  const initialRangeRef = useRef<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const childTableScrollRef = useRef<HTMLDivElement>(null);
  const formScrollPaneRef = useRef<HTMLDivElement>(null);
  const [formBodyScrolled, setFormBodyScrolled] = useState(false);
  const [formBodyAtBottom, setFormBodyAtBottom] = useState(true);
  const lastAddedChildIdRef = useRef<string | number | null>(null);
  const [childsColumns, setChildsColumns] = useState<ColumnItem[]>(initialColumns);
  const [saving, setSaving] = useState(false);
  const [checklistItems, setChecklistItems] = useState<Array<{ id: string; ten: string; isCheck: boolean; dateTime: string }>>([]);
  const checklistSyncTimeoutsRef = useRef<Record<string, number>>({});
  // Thảo luận
  const [discussItems, setDiscussItems] = useState<DiscussItem[]>([]);
  const [discussLoading, setDiscussLoading] = useState(false);
  const [discussContent, setDiscussContent] = useState('');
  const [discussFiles, setDiscussFiles] = useState<File[]>([]);
  const [replyParentId, setReplyParentId] = useState<string | null>(null);
  const [sendingDiscuss, setSendingDiscuss] = useState(false);
  const discussFileInputRef = useRef<HTMLInputElement | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [sendingReply, setSendingReply] = useState(false);
  const [highlightDiscussId, setHighlightDiscussId] = useState<string | null>(null);
  const pendingNewDiscussRef = useRef<PendingNewDiscussMark | null>(null);
  const highlightDiscussTimerRef = useRef<number | null>(null);
  const replyFileInputRef = useRef<HTMLInputElement | null>(null);
  const discussTextAreaRef = useRef<any>(null);
  const replyTextAreaRef = useRef<any>(null);
  const [thaoLuanMention, setThaoLuanMention] = useState<ThaoLuanMentionState | null>(null);
  const [thaoLuanMentionIndex, setThaoLuanMentionIndex] = useState(0);
  const [thaoLuanMentionMenuPos, setThaoLuanMentionMenuPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const viTriBaseRef = useRef<Map<string, CongViecDuAnCheck>>(new Map());
  const [fullCheckList, setFullCheckList] = useState<ViTriCheckItem[]>([]);

  const currentCongViecId = String(data?.duAnCongViec?.congViec?.id ?? '');
  const siblingParentId = String(data?.duAnCongViec?.congViec?.parentID ?? idDuAn ?? '');

  useEffect(() => {
    if (discussionOnly) return;
    const normalized = (data?.listCongViecDuAnCheck ?? [])
      .map(normalizeCheckItem)
      .filter((x) => x.id);
    viTriBaseRef.current = new Map(
      normalized.map((x) => [x.id, { ...x, hasChange: false }])
    );
    setFullCheckList(normalized.map((x) => ({ ...x })));
  }, [data?.listCongViecDuAnCheck, data?.duAnCongViec?.congViec?.id, discussionOnly]);

  const resolveTenCongViec = useCallback(
    (item: ViTriCheckItem) => {
      if (item.tenCongViec?.trim()) return item.tenCongViec.trim();
      if (item.id === currentCongViecId) {
        return data?.duAnCongViec?.congViec?.tenCongViec || item.id;
      }
      const fromParent = data?.listCongViecParent?.[item.id];
      if (fromParent) return fromParent;
      const fromChild = data?.listCongViecChild?.[item.id];
      if (fromChild) return fromChild;
      return item.id;
    },
    [data, currentCongViecId]
  );

  const siblingViTriItems = useMemo(
    () =>
      fullCheckList
        .filter((x) => String(x.parentID) === siblingParentId && !(x as any).isDuAn)
        .sort((a, b) => (Number(a.viTri) || 0) - (Number(b.viTri) || 0))
        .map((x) => ({ ...x, tenCongViec: resolveTenCongViec(x) })),
    [fullCheckList, siblingParentId, resolveTenCongViec]
  );

  const handleSiblingViTriChange = useCallback(
    (reordered: ViTriCheckItem[]) => {
      const patchById = new Map(reordered.map((x) => [x.id, x]));
      setFullCheckList((prev) =>
        prev.map((item) => {
          const patch = patchById.get(item.id);
          if (!patch) return item;
          const base = viTriBaseRef.current.get(item.id);
          const hasChange = base ? base.viTri !== patch.viTri : true;
          return { ...item, viTri: patch.viTri, hasChange };
        })
      );
      if (form && currentCongViecId) {
        const cur = reordered.find((x) => x.id === currentCongViecId);
        if (cur?.viTri != null) form.setFieldValue('viTri', cur.viTri);
      }
    },
    [form, currentCongViecId]
  );

  const currentCongViecIdRef = useRef<string>('');
  const loadThaoLuanRef = useRef<() => Promise<void>>(async () => { });

  // SignalR realtime for discussion
  const { joinGroup, leaveGroup, sendMessageToGroup } = useSignalR({
    onGroupMessage: (groupName, fromUserId, message) => {
      const currentId = currentCongViecIdRef.current;
      const expectedGroup = currentId ? `congviec-thaoluan-${currentId}` : '';
      if (expectedGroup && groupName === expectedGroup) {
        void loadThaoLuanRef.current();
        return;
      }
      // Tùy hub implementation, payload có thể nằm ở message hoặc fromUserId
      try {
        const rawPayload =
          typeof message === 'string' && message
            ? message
            : typeof fromUserId === 'string' && fromUserId
              ? fromUserId
              : '';

        const payload = rawPayload ? JSON.parse(rawPayload) : {};
        if (
          payload?.type === 'thaoLuan' &&
          payload?.congViecId &&
          String(payload.congViecId) === String(currentId)
        ) {
          void loadThaoLuanRef.current();
        }
      } catch {
        // ignore invalid JSON
      }
    },
  });
  // HinhThucTongHop: 0=HieuSuat, 1=NhapTay, 2=TongCon, 3=TrungBinhCon
  // Default: không có childs = 1 (NhapTay), có childs = 0 (HieuSuat)
  const [hinhThucTongHop, setHinhThucTongHop] = useState<number>(childs && childs.length > 0 ? 0 : 1);

  const apiChildsCount = useMemo(() => {
    const raw = data?.duAnCongViec?.childs;
    return Array.isArray(raw) ? raw.length : 0;
  }, [data?.duAnCongViec?.childs]);

  const hinhThucTongHopOptions = useMemo(
    () =>
      apiChildsCount > 0
        ? [
            { label: 'Hiệu suất tỉ trọng', value: 0 },
            { label: 'Báo cáo nhập tay', value: 1 },
            { label: 'Tổng con', value: 2 },
            { label: 'Trung bình con', value: 3 },
          ]
        : [{ label: 'Báo cáo nhập tay', value: 1 }],
    [apiChildsCount],
  );

  // State cho Popover chi tiêu
  const [chiTieuPopoverVisible, setChiTieuPopoverVisible] = useState(false);
  const [chiTieuValue, setChiTieuValue] = useState<number>(0);
  const [idDoLuongValue, setIdDoLuongValue] = useState<string>('');
  const [tanSuatValue, setTanSuatValue] = useState<number>(1);
  const [chiaChiTieuModalVisible, setChiaChiTieuModalVisible] = useState(false);
  /** Tần suất đã lưu trên server (snapshot khi load data) — dùng cảnh báo chia lại giống cột Tần suất bảng */
  const savedTanSuatRef = useRef<number | undefined>(undefined);
  /** Hình thức chia chỉ tiêu (0 Tổng / 1 Trung bình / 2 Tiến độ) — lưu cùng editcongviecs */
  const [hinhThucChiaChiTieu, setHinhThucChiaChiTieu] = useState<number>(0);
  const [listChiTieuDraft, setListChiTieuDraft] = useState<DuAnCongViecChiTieuItem[]>([]);
  // Ngân sách (unit) đang edit
  const [unitValueState, setUnitValueState] = useState<number | undefined>(undefined);

  // State cho mô tả công việc
  const [descriptionValue, setDescriptionValue] = useState<string>('');
  const [descriptionEditorKey, setDescriptionEditorKey] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<{ fileName: string; filePath: string } | null>(null);

  // State cho drag/drop upload tài liệu đính kèm (tải thẳng lên File Manager, không cần mở Finder)
  const [attachmentUploads, setAttachmentUploads] = useState<ClientUploadItem[]>([]);
  const [isFileDragActive, setIsFileDragActive] = useState(false);
  const fileDragDepthRef = useRef(0);

  const parseFileLinks = (rawVal: unknown): string[] => {
    if (Array.isArray(rawVal)) {
      return rawVal.map((u) => String(u).trim()).filter(Boolean);
    }
    if (typeof rawVal === 'string' && rawVal.trim()) {
      return rawVal
        .split(',')
        .map((u) => u.trim())
        .filter(Boolean);
    }
    return [];
  };

  const dedupeFileLinks = (list: string[]): string[] => {
    const seen = new Set<string>();
    return list.filter((u) => {
      if (seen.has(u)) return false;
      seen.add(u);
      return true;
    });
  };

  const filesDinhKemRaw = Form.useWatch('filesDinhKem', form);
  const unitFormValue = Form.useWatch('unit', form);
  const fileAttachCount = useMemo(
    () => parseFileLinks(filesDinhKemRaw ?? form?.getFieldValue('filesDinhKem')).length,
    [filesDinhKemRaw, form]
  );
  const uploadingAttachments = useMemo(() => hasPendingUploads(attachmentUploads), [attachmentUploads]);

  // ==================== Drag/drop upload file ====================
  const isFileDrag = (event: React.DragEvent<HTMLElement>) => {
    const dt = event.dataTransfer;
    if (!dt) return false;
    return Array.from(dt.types || []).includes('Files') || (dt.files && dt.files.length > 0);
  };

  const handleFileDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isEdit || !isFileDrag(event)) return;
    event.preventDefault();
    fileDragDepthRef.current += 1;
    setIsFileDragActive(true);
  };

  const handleFileDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isEdit || !isFileDrag(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleFileDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isEdit || !isFileDrag(event)) return;
    event.preventDefault();
    fileDragDepthRef.current = Math.max(0, fileDragDepthRef.current - 1);
    if (fileDragDepthRef.current === 0) setIsFileDragActive(false);
  };

  const handleFileDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    if (!isEdit || !isFileDrag(event)) return;
    event.preventDefault();
    fileDragDepthRef.current = 0;
    setIsFileDragActive(false);

    const files = Array.from(event.dataTransfer.files || []);
    if (files.length === 0) return;

    const uploads = createUploadItems(files);
    setAttachmentUploads((current) => [...current, ...uploads]);

    try {
      const result = await FinderJs.uploadToActionPlan(uploads, (progress) => {
        setAttachmentUploads((current) => applyUploadProgress(current, progress));
      });
      setAttachmentUploads((current) =>
        applyUploadResults(current, result.files).filter((item) => item.status !== 'success')
      );

      const uploadedFiles = result.files
        .filter((item) => item.status === 'success' && item.file)
        .map((item) => item.file);
      const urls = mapFinderSelectionToFileUrls(uploadedFiles);
      if (urls.length > 0) {
        if (!form) {
          message.error('Không thể lưu file đính kèm: form chưa sẵn sàng');
          return;
        }
        const currentFiles = parseFileLinks(form.getFieldValue('filesDinhKem'));
        const next = dedupeFileLinks([...currentFiles, ...urls]);
        form.setFieldValue('filesDinhKem', next);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload thất bại';
      setAttachmentUploads((current) => failUnresolvedUploads(current, errorMessage));
    }
  };

  // Upload ảnh dán vào editor mô tả → trả về URL file đã lưu trên File Manager
  const handleUploadImage = useCallback(async (file: File): Promise<string> => {
    const result = await FinderJs.uploadToActionPlan([{ clientId: `img-${Date.now()}-${Math.random().toString(36).slice(2)}`, file }]);
    const uploaded = result.files.find((f) => f.status === 'success' && f.file);
    if (!uploaded?.file) {
      const failed = result.files.find((f) => f.status === 'error');
      throw new Error(failed?.error || 'Upload ảnh thất bại');
    }
    const url = mapFinderSelectionToFileUrls([uploaded.file])[0];
    if (!url) throw new Error('Upload ảnh thất bại');
    return url;
  }, []);

  // Unit helpers
  const { profile } = useAuthStore();
  const tenantID = (profile as any)?.tenantID || '';
  const {
    units,
    ready: orgReady,
    loadEmployee,
    loadDepartmentData,
    loadPositionData,
  } = useLocalFilter();
  const unitHelpers = useMemo(() => createUnitHelpers(units, tenantID), [units, tenantID]);

  /** Selector để luôn nhận cập nhật khi form đổi (ổn định hơn useWatch từng field trong modal) */
  const mentionNvFormSlice = Form.useWatch(
    (values: any) => ({
      nv: values?.nv,
      nvSupport: values?.nvSupport,
      nvNguoiTao: values?.nvNguoiTao,
    }),
    form
  ) as { nv?: unknown; nvSupport?: unknown } | undefined;

  const watchedNguoiHoTro = mentionNvFormSlice?.nvSupport;
  const watchedNguoiPhuTrach = mentionNvFormSlice?.nv;

  const replyMentionAssignableIds = useMemo(() => {
    const ids = new Set<string>();
    const add = (raw: unknown) => {
      for (const id of normalizeFormNvIds(raw)) {
        if (id) ids.add(id);
      }
    };
    add(mentionNvFormSlice?.nvSupport);
    add(mentionNvFormSlice?.nv);
    const cv = data?.duAnCongViec?.congViec;
    if (cv) {
      add(cv.nguoiPhuTrachID);
      if (typeof cv.nguoiHoTroID === 'string') {
        add(cv.nguoiHoTroID.split(','));
      } else {
        add(cv.nguoiHoTroID);
      }
    }
    if (parentCongViec) {
      add(parentCongViec.nguoiPhuTrachID);
      if (typeof parentCongViec.nguoiHoTroID === 'string') {
        add(parentCongViec.nguoiHoTroID.split(','));
      } else {
        add(parentCongViec.nguoiHoTroID);
      }
    }
    return [...ids];
  }, [
    mentionNvFormSlice?.nv,
    mentionNvFormSlice?.nvSupport,
    data?.duAnCongViec?.congViec,
    parentCongViec,
  ]);

  const replyMentionOptionRows = useMemo(() => {
    const rows: ThaoLuanMentionOptionRow[] = [
      {
        key: '__all',
        id: 'all',
        label: 'Tất cả',
        insert: thaoLuanMentionInsertFromDisplayLabel('Tất cả'),
        search: 'all tat ca tất cả',
        payloadToken: '@all',
      },
    ];
    const pushNvFallbackRow = (id: string) => {
      rows.push({
        key: id,
        id,
        label: 'Nhân viên',
        insert: thaoLuanMentionInsertFromDisplayLabel('Nhân viên'),
        search: `${id}`,
        payloadToken: `@NhanVien_${id}`,
      });
    };
    for (const id of replyMentionAssignableIds) {
      if (!orgReady) {
        pushNvFallbackRow(id);
        continue;
      }
      const emp: any = loadEmployee(id);
      if (!emp) {
        pushNvFallbackRow(id);
        continue;
      }
      const name = emp.TenNhanVien || emp.tenNhanVien || '';
      const login = String(
        emp.email || emp.tenTruyCap || emp.MaNhanVien || emp.maNhanVien || id
      ).trim();
      const bpid = emp.BPID || emp.bpid || '';
      const cvid = emp.CVID || emp.cvid || '';
      const bp = bpid
        ? String(loadDepartmentData(bpid, 'Ten') || loadDepartmentData(bpid, 'ten') || '')
        : '';
      const cv = cvid
        ? String(loadPositionData(cvid, 'Ten') || loadPositionData(cvid, 'ten') || '')
        : '';
      const displayName = String(name || '').trim() || 'Nhân viên';
      const label =
        formatThaoLuanMentionDisplayLabel(displayName, bp, cv) || displayName;
      const search = `${id} ${login} ${name} ${bp} ${cv}`;
      rows.push({
        key: id,
        id,
        label,
        insert: thaoLuanMentionInsertFromDisplayLabel(label),
        search,
        payloadToken: `@${label.replace(/\s+/g, '')}_${id}`,
      });
    }
    return rows;
  }, [
    replyMentionAssignableIds,
    orgReady,
    loadEmployee,
    loadDepartmentData,
    loadPositionData,
  ]);

  const thaoLuanMentionFiltered = useMemo(() => {
    if (!thaoLuanMention) return [];
    const q = thaoLuanMention.query.trim();
    const qn = normMentionSearch(q);
    if (!qn) return replyMentionOptionRows;
    return replyMentionOptionRows.filter((r) => {
      const hay = normMentionSearch(r.search + ' ' + r.label);
      return hay.includes(qn);
    });
  }, [thaoLuanMention, replyMentionOptionRows]);

  const mentionRowsForPayload = useMemo(
    () =>
      replyMentionOptionRows
        .filter((r) => r.id && r.label)
        .slice()
        .sort((a, b) => b.label.length - a.label.length),
    [replyMentionOptionRows]
  );

  const buildThaoLuanPayloadContent = useCallback(
    (text: string) => {
      let next = String(text || '');
      mentionRowsForPayload.forEach((row) => {
        const escapedLabel = row.label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const labelRegex = new RegExp(`(^|\\s)@${escapedLabel}(?=$|\\s|[.,!?;:])`, 'g');
        next = next.replace(
          labelRegex,
          (_match, lead: string) => `${lead}${row.payloadToken}`
        );
      });
      // Chuẩn hóa token mention: @label_id => @id (giữ nguyên @all)
      return next.replace(
        /@[^@\s_]+_([^\s@_.,!?;:]+)([.,!?;:]?)/g,
        (_match, id: string, punctuation: string) => `@${id}${punctuation || ''}`
      );
    },
    [mentionRowsForPayload]
  );

  const thaoLuanMentionListboxStyle = useMemo((): React.CSSProperties | null => {
    if (!thaoLuanMentionMenuPos) return null;
    return {
      position: 'fixed',
      top: `calc(${thaoLuanMentionMenuPos.top}px - 50px)`,
      left: thaoLuanMentionMenuPos.left,
      width: thaoLuanMentionMenuPos.width,
      zIndex: THAO_LUAN_MENTION_LISTBOX_Z,
      maxHeight: 155,
      overflowY: 'auto',
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 4,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
    };
  }, [thaoLuanMentionMenuPos]);

  const thaoLuanMentionOptionBaseStyle = useMemo(
    (): React.CSSProperties => ({
      padding: '8px 10px',
      fontSize: 13,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
    []
  );

  const onThaoLuanMentionItemMouseEnter = useCallback((idx: number) => {
    setThaoLuanMentionIndex(idx);
  }, []);

  const syncThaoLuanMentionFromTextareaEl = useCallback(
    (el: HTMLTextAreaElement, surface: ThaoLuanMentionSurface) => {
      const caret = el.selectionStart ?? el.value.length;
      setThaoLuanMention(thaoLuanMentionStateFromCaret(el.value, caret, surface));
    },
    []
  );

  const applyThaoLuanMentionPick = useCallback(
    (
      row: { insert: string },
      currentText: string,
      mention: { start: number },
      caret: number,
      surface: ThaoLuanMentionSurface
    ) => {
      const insertion = row.insert.endsWith(' ')
        ? row.insert
        : `${row.insert} `;
      const before = currentText.slice(0, mention.start);
      const after = currentText.slice(caret);
      const next = (before + insertion + after).slice(0, 500);
      if (surface === 'discuss') {
        setDiscussContent(next);
      } else {
        setReplyContent(next);
      }
      setThaoLuanMention(null);
      requestAnimationFrame(() => {
        const ref = surface === 'discuss' ? discussTextAreaRef : replyTextAreaRef;
        const el = ref.current?.resizableTextArea
          ?.textArea as HTMLTextAreaElement | undefined;
        if (!el) return;
        const pos = Math.min(before.length + insertion.length, next.length);
        el.setSelectionRange(pos, pos);
        el.focus();
      });
    },
    []
  );

  const onThaoLuanMentionItemMouseDown = useCallback(
    (ev: React.MouseEvent, row: ThaoLuanMentionOptionRow) => {
      ev.preventDefault();
      const mention = thaoLuanMention;
      if (!mention) return;
      const surface = mention.surface;
      const ref = surface === 'discuss' ? discussTextAreaRef : replyTextAreaRef;
      const text = surface === 'discuss' ? discussContent : replyContent;
      const ta = ref.current?.resizableTextArea?.textArea as
        | HTMLTextAreaElement
        | undefined;
      const caret = ta?.selectionStart ?? text.length;
      applyThaoLuanMentionPick(row, text, mention, caret, surface);
    },
    [thaoLuanMention, discussContent, replyContent, applyThaoLuanMentionPick]
  );

  const handleThaoLuanMentionListKeyDown = useCallback(
    (
      e: React.KeyboardEvent<HTMLTextAreaElement>,
      surface: ThaoLuanMentionSurface,
      currentText: string,
      areaRef: React.MutableRefObject<any>
    ) => {
      if (
        thaoLuanMention?.surface !== surface ||
        thaoLuanMentionFiltered.length === 0
      ) {
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setThaoLuanMentionIndex((i) =>
          Math.min(i + 1, thaoLuanMentionFiltered.length - 1)
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setThaoLuanMentionIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const row = thaoLuanMentionFiltered[thaoLuanMentionIndex];
        const mention = thaoLuanMention;
        if (!row || !mention) return;
        const ta = areaRef.current?.resizableTextArea?.textArea as
          | HTMLTextAreaElement
          | undefined;
        const caret = ta?.selectionStart ?? currentText.length;
        applyThaoLuanMentionPick(row, currentText, mention, caret, surface);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setThaoLuanMention(null);
      }
    },
    [
      thaoLuanMention,
      thaoLuanMentionFiltered,
      thaoLuanMentionIndex,
      applyThaoLuanMentionPick,
    ]
  );

  useEffect(() => {
    setThaoLuanMentionIndex(0);
  }, [thaoLuanMention?.surface, thaoLuanMention?.start, thaoLuanMention?.query, thaoLuanMentionFiltered.length]);

  useLayoutEffect(() => {
    if (!thaoLuanMention || thaoLuanMentionFiltered.length === 0) {
      setThaoLuanMentionMenuPos(null);
      return;
    }
    const update = () => {
      const ref =
        thaoLuanMention.surface === 'discuss'
          ? discussTextAreaRef
          : replyTextAreaRef;
      const inner = ref.current?.resizableTextArea
        ?.textArea as HTMLTextAreaElement | undefined;
      if (!inner) {
        setThaoLuanMentionMenuPos(null);
        return;
      }
      const rect = inner.getBoundingClientRect();
      setThaoLuanMentionMenuPos({
        top: rect.bottom + 4,
        left: rect.left + 8,
        width: Math.min(Math.max(rect.width - 16, 280), 420),
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [
    thaoLuanMention,
    thaoLuanMentionFiltered.length,
    discussContent,
    replyContent,
    replyParentId,
  ]);

  useEffect(() => {
    if (discussionOnly) return;
    const init = form?.getFieldValue('datetime');
    // Luôn lưu frange ban đầu của parent: nếu chỉ có 1 ngày thì start = end = ngày đó
    if (init && Array.isArray(init) && init[0]) {
      const start = dayjs(init[0]);
      const end = init[1] ? dayjs(init[1]) : start;
      initialRangeRef.current = [start, end]; // Lưu giá trị ban đầu
    }
  }, [form, discussionOnly]);

  // Sync description từ data prop — đọc trực tiếp từ data thay vì form.getFieldValue
  // vì child useEffect chạy trước parent useEffect nên form chưa kịp setFieldsValue khi effect này chạy
  useEffect(() => {
    if (discussionOnly) return;
    if (!data) {
      setDescriptionEditorKey(null);
      return;
    }
    const congViecId = data.duAnCongViec?.congViec?.id;
    const desc = data.duAnCongViec?.congViec?.moTa ?? '';
    setDescriptionValue(desc);
    form?.setFieldValue('description', desc);
    setDescriptionEditorKey(String(congViecId ?? '__new__'));
  }, [data, data?.duAnCongViec?.congViec?.id, data?.duAnCongViec?.congViec?.moTa, form, discussionOnly]);

  // Theo dõi thay đổi datetime của parent để cập nhật initialRange cho các child picker
  const parentDatetime = (Form as any).useWatch
    ? (Form as any).useWatch('datetime', form)
    : form?.getFieldValue('datetime');

  // Tính toán initialRange từ parentDatetime để truyền vào child picker
  const currentInitialRange: [dayjs.Dayjs, dayjs.Dayjs] | null = useMemo(() => {
    if (!parentDatetime || !Array.isArray(parentDatetime) || !parentDatetime[0]) {
      return initialRangeRef.current;
    }
    const newParentStart = dayjs(parentDatetime[0]);
    const newParentEnd = parentDatetime[1] ? dayjs(parentDatetime[1]) : newParentStart;
    // Cập nhật ref để dùng cho các trường hợp khác
    initialRangeRef.current = [newParentStart, newParentEnd];
    return [newParentStart, newParentEnd];
  }, [parentDatetime]);

  // ==================== Ngân sách ====================
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const nganSachToolTip = form?.getFieldValue('nganSachToolTip');
  const phanTramNganSach = form?.getFieldValue('phanTramNganSach');

  // Toolbar/list phụ trách + selectListMucTieu từ API chi tiết (mục tiêu ngoài phạm vi phụ trách).
  const targetOptions = useMemo(() => {
    const fromProp = buildTargetOptionsFromSelectList(selectListMucTieu);
    const fromDetailApi = buildTargetOptionsFromSelectList(
      data?.selectListMucTieu as SelectList | SelectListItem[] | undefined
    );
    const currentMtId = String(data?.duAnCongViec?.congViec?.mtctid ?? '').trim();
    const fallbackCurrent: TargetSelectOption[] = [];
    if (currentMtId) {
      const known = mergeTargetOptionLists([fromProp, fromDetailApi]);
      const existing = known.find((o) => o.value === currentMtId);
      if (!existing || existing.label === currentMtId) {
        const label = pickBetterTargetLabel(
          existing?.label ?? '',
          fromDetailApi.find((o) => o.value === currentMtId)?.label ?? '',
          currentMtId
        );
        if (label !== currentMtId || !existing) {
          fallbackCurrent.push({ value: currentMtId, label });
        }
      }
    }
    return mergeTargetOptionLists([fromProp, fromDetailApi, fallbackCurrent]);
  }, [selectListMucTieu, data?.selectListMucTieu, data?.duAnCongViec?.congViec?.mtctid]);

  // Set default target = item đầu tiên khi tạo mới và chưa có giá trị
  useEffect(() => {
    if (discussionOnly) return;
    if (!isEdit && form && targetOptions.length > 0) {
      const currentTarget = form.getFieldValue('idMucTieu');
      if (!currentTarget) {
        const firstOption = targetOptions[0];
        if (firstOption?.value) {
          form.setFieldValue('idMucTieu', firstOption.value);
        }
      }
    }
  }, [isEdit, form, targetOptions, discussionOnly]);

  // Load childs into columns when childs prop changes
  useEffect(() => {
    if (discussionOnly) return;
    if (childs && childs.length > 0) {
      const mappedColumns: ColumnItem[] = childs.map((child, index) => {
        const congViec = child.congViec;
        const thoiGian = child.thoiGian || '';

        // Format date from thoiGian or use ngayBatDau/ngayKetThuc
        let dateTime = thoiGian;
        if (congViec.ngayBatDau && congViec.ngayKetThuc) {
          dateTime = `${dayjs(congViec.ngayBatDau).format('DD/MM/YYYY')} - ${dayjs(congViec.ngayKetThuc).format(
            'DD/MM/YYYY'
          )}`;
        } else if (thoiGian) {
          dateTime = thoiGian;
        }

        const rawPhuTrach =
          congViec.nguoiPhuTrachID ?? (congViec as { NguoiPhuTrachID?: string }).NguoiPhuTrachID ?? '';
        const person = firstPhuTrachIdForChildDefault(rawPhuTrach);
        const id =
          congViec.id != null && String(congViec.id).trim() !== ''
            ? String(congViec.id)
            : `row_${index}`;

        const completionCount = child.countChilds || 0;
        const completionPercentage = Math.round(child.trongKy?.hoanThanh || 0);

        return {
          id,
          projectName: congViec.tenCongViec || '',
          person,
          dateTime,
          completionCount,
          completionPercentage,
          originalData: child,
        };
      });
      setChildsColumns(mappedColumns);
    } else {
      setChildsColumns(initialColumns);
    }
  }, [childs, discussionOnly]);

  const congViecChecklistId =
    data?.duAnCongViec?.congViec?.id ||
    (parentCongViec && (parentCongViec.congViecID || parentCongViec.congViecId || parentCongViec.id)) ||
    idDuAn ||
    '';

  const resolveUserName = useCallback(
    (userId: string | number | undefined) => {
      if (!userId) return 'Người dùng';
      const employees = (selectListDuAn as any) || [];
      const nv = Array.isArray(employees)
        ? employees.find((n: any) => String(n.id) === String(userId))
        : null;
      return nv?.fullName || nv?.hoTen || nv?.ten || String(userId);
    },
    [selectListDuAn]
  );

  const buildChildEmployeeInfo = useCallback(
    (personRaw: string | undefined): EmployeeInfo | null => {
      const id = firstPhuTrachIdForChildDefault(personRaw);
      if (!id) return null;
      if (orgReady) {
        const emp: any = loadEmployee(id);
        if (emp) {
          const bpid = emp.BPID || emp.bpid || '';
          const cvid = emp.CVID || emp.cvid || '';
          return {
            id,
            tenNhanVien: emp.TenNhanVien || emp.tenNhanVien || resolveUserName(id),
            maNhanVien: emp.MaNhanVien || emp.maNhanVien || '',
            hinh: emp.Hinh || emp.hinh || '',
            boPhan: bpid
              ? String(loadDepartmentData(bpid, 'Ten') || loadDepartmentData(bpid, 'ten') || '')
              : '',
            chucVu: cvid
              ? String(loadPositionData(cvid, 'Ten') || loadPositionData(cvid, 'ten') || '')
              : '',
          };
        }
      }
      return { id, tenNhanVien: resolveUserName(id) };
    },
    [orgReady, loadEmployee, loadDepartmentData, loadPositionData, resolveUserName]
  );

  const loadThaoLuan = useCallback(async () => {
    if (!congViecChecklistId) return;
    setDiscussLoading(true);
    try {
      const res: any = await baocaoApi.loadThaoLuan({
        id: congViecChecklistId,
        phanHe: 'bsc',
      });
      if (!res?.success || !res.data) {
        setDiscussItems([]);
        return;
      }
      const list = Array.isArray(res.data.list) ? res.data.list : [];

      const getParentKey = (parentId: any) =>
        parentId && String(parentId) !== '0' ? String(parentId) : '0';

      const byParent = new Map<string, any[]>();
      list.forEach((c: any) => {
        const key = getParentKey(c.parentID);
        if (!byParent.has(key)) byParent.set(key, []);
        byParent.get(key)!.push(c);
      });

      byParent.forEach((arr) => {
        arr.sort((a: any, b: any) => {
          const ta = dayjs(a.ngayTao).valueOf();
          const tb = dayjs(b.ngayTao).valueOf();
          return tb - ta;
        });
      });

      const flattened: DiscussItem[] = [];
      const walk = (parentKey: string, depth: number) => {
        const children = byParent.get(parentKey) || [];
        children.forEach((c: any) => {
          const rawFiles: string = c.files || c.Files || c.filePaths || '';
          const fileUrls = String(rawFiles)
            .split(',')
            .map((u) => u.trim())
            .filter((u) => !!u);

          const attachments =
            fileUrls.length > 0
              ? fileUrls.map((url: string) => ({
                url,
                name: extractFileNameFromUrl(url) || 'Tài liệu',
              }))
              : [];

          flattened.push({
            id: c.id,
            authorId: c.nguoiTao,
            author: resolveUserName(c.nguoiTao),
            time: c.ngayTao ? dayjs(c.ngayTao).format('DD/MM/YYYY HH:mm') : '',
            content: c.noiDung || '',
            parentId: getParentKey(c.parentID) === '0' ? undefined : String(c.parentID),
            depth,
            isReply: depth > 0,
            avatarUrl: c.hinh || c.avatar || c.avatarUrl || c.nguoiTaoHinh || c.nguoiTaoAvatar || '',
            attachments,
          });

          walk(String(c.id), depth + 1);
        });
      };

      walk('0', 0);
      setDiscussItems(flattened);
    } catch (error) {
      console.error('loadThaoLuan error', error);
    } finally {
      setDiscussLoading(false);
    }
  }, [congViecChecklistId, resolveUserName]);

  useEffect(() => {
    currentCongViecIdRef.current = String(congViecChecklistId || '');
  }, [congViecChecklistId]);

  useEffect(() => {
    loadThaoLuanRef.current = loadThaoLuan;
  }, [loadThaoLuan]);

  const markDiscussAsNew = useCallback((id: string) => {
    if (highlightDiscussTimerRef.current) {
      window.clearTimeout(highlightDiscussTimerRef.current);
    }
    setHighlightDiscussId(id);
    highlightDiscussTimerRef.current = window.setTimeout(() => {
      setHighlightDiscussId((cur) => (cur === id ? null : cur));
      highlightDiscussTimerRef.current = null;
    }, 8000);
  }, []);

  useEffect(() => {
    const pending = pendingNewDiscussRef.current;
    if (!pending || discussLoading) return;

    let targetId: string | null = null;
    if (pending.kind === 'id') {
      targetId = pending.id;
    } else if (pending.kind === 'newestUnderParent') {
      const found = discussItems.find((i) => String(i.parentId) === String(pending.parentId));
      targetId = found?.id != null ? String(found.id) : null;
    } else {
      const found = discussItems.find((i) => !i.parentId);
      targetId = found?.id != null ? String(found.id) : null;
    }

    if (!targetId || !discussItems.some((i) => String(i.id) === targetId)) return;

    pendingNewDiscussRef.current = null;
    markDiscussAsNew(targetId);
  }, [discussItems, discussLoading, markDiscussAsNew]);

  useEffect(
    () => () => {
      if (highlightDiscussTimerRef.current) {
        window.clearTimeout(highlightDiscussTimerRef.current);
      }
    },
    []
  );

  const scheduleChecklistSync = (item: { id: string; ten: string; isCheck: boolean }) => {
    if (!congViecChecklistId) return;
    const key = String(item.id);
    const timeouts = checklistSyncTimeoutsRef.current;
    if (timeouts[key]) {
      window.clearTimeout(timeouts[key]);
    }
    timeouts[key] = window.setTimeout(async () => {
      delete timeouts[key];
      try {
        const isTemp = key.startsWith('temp');
        if (isTemp) {
          const addPayload = {
            congViecID: congViecChecklistId,
            congViecId: congViecChecklistId,
            CongViecId: congViecChecklistId,
            tenChecklist: item.ten,
            ten: item.ten,
            Ten: item.ten,
            isCheck: item.isCheck,
            IsCheck: item.isCheck,
          };
          const response: any = await congviecduanApi.addChecklist(addPayload);
          if (response?.success && response.data) {
            const newId =
              response.data.checklistId ||
              response.data.ChecklistId ||
              response.data.id ||
              response.data.congViecChecklistId ||
              response.data.congViecChecklistID ||
              response.data.congViecChecklist?.id;
            if (newId) {
              setChecklistItems((prev) =>
                prev.map((i) =>
                  i.id === key
                    ? {
                      ...i,
                      id: String(newId),
                      dateTime: i.dateTime || dayjs().format('DD/MM/YYYY'),
                    }
                    : i
                )
              );
              message.success('Đã thêm checklist');
            }
          }
        } else {
          const updatePayload = {
            checklistId: item.id,
            ChecklistId: item.id,
            congViecID: congViecChecklistId,
            congViecId: congViecChecklistId,
            CongViecId: congViecChecklistId,
            tenChecklist: item.ten,
            ten: item.ten,
            Ten: item.ten,
            isCheck: item.isCheck,
            IsCheck: item.isCheck,
          };
          const res: any = await congviecduanApi.updateChecklist(updatePayload);
          if (res?.success) {
            message.success('Đã cập nhật checklist');

          }
        }
      } catch (error) {
        console.error('Checklist sync error', error);
        message.error('Không thể lưu checklist. Vui lòng thử lại.');
      }
    }, CHECKLIST_SYNC_TIMEOUT);
  };

  useEffect(() => {
    if (!congViecChecklistId) return;

    // Join SignalR group theo ID công việc để nhận realtime
    const groupName = `congviec-thaoluan-${congViecChecklistId}`;
    joinGroup(groupName).catch((err) => {
      console.error('Join discussion group error', err);
    });
    loadThaoLuan();

    return () => {
      leaveGroup(groupName).catch((err) => {
        console.error('Leave discussion group error', err);
      });
    };
    // Không đưa joinGroup/leaveGroup vào deps để tránh effect bị gọi lại vô hạn
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [congViecChecklistId, loadThaoLuan]);

  const handleDiscussFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setDiscussFiles(files);
  };

  const handleReplyFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setReplyFiles(files);
  };

  const handleSendDiscussion = async () => {
    const content = discussContent.trim();
    if (!content) {
      message.warning('Vui lòng nhập nội dung trước khi gửi');
      return;
    }
    if (!congViecChecklistId) {
      message.error('Không xác định được công việc để thảo luận');
      return;
    }
    setSendingDiscuss(true);
    try {
      const payloadContent = buildThaoLuanPayloadContent(content);
      const formData = new FormData();
      formData.append('ID', congViecChecklistId);
      formData.append('ParentID', '0');
      formData.append('NoiDung', payloadContent);
      formData.append('PhanHe', 'bsc');
      discussFiles.forEach((file) => formData.append('files', file));

      const res: any = await baocaoApi.saveThaoLuan(formData);
      if (res?.success && res.data) {
        const savedId = extractSavedThaoLuanId(res);
        pendingNewDiscussRef.current = savedId
          ? { kind: 'id', id: savedId }
          : { kind: 'newestRoot' };
        setDiscussContent('');
        setThaoLuanMention(null);
        setDiscussFiles([]);
        const groupName = `congviec-thaoluan-${congViecChecklistId}`;
        try {
          await sendMessageToGroup(
            groupName,
            JSON.stringify({ type: 'thaoLuan', congViecId: congViecChecklistId, at: Date.now() })
          );
        } catch (err) {
          console.warn('broadcast group message failed', err);
        }
        await loadThaoLuan();
        message.success('Đã gửi thảo luận');
      } else {
        message.error('Gửi thảo luận không thành công');
      }
    } catch (error) {
      console.error('saveThaoLuan error', error);
      message.error('Có lỗi khi gửi thảo luận');
    } finally {
      setSendingDiscuss(false);
    }
  };

  const handleSendReply = async () => {
    const content = replyContent.trim();
    if (!replyParentId) {
      message.warning('Vui lòng chọn bình luận cần phản hồi');
      return;
    }
    if (!content) {
      message.warning('Vui lòng nhập nội dung phản hồi trước khi gửi');
      return;
    }
    if (!congViecChecklistId) {
      message.error('Không xác định được công việc để thảo luận');
      return;
    }

    setSendingReply(true);
    const parentIdSnapshot = replyParentId;
    try {
      const payloadContent = buildThaoLuanPayloadContent(content);
      const formData = new FormData();
      formData.append('ID', congViecChecklistId);
      formData.append('ParentID', replyParentId);
      formData.append('NoiDung', payloadContent);
      formData.append('PhanHe', 'bsc');
      replyFiles.forEach((file) => formData.append('files', file));

      const res: any = await baocaoApi.saveThaoLuan(formData);
      if (res?.success && res.data) {
        const savedId = extractSavedThaoLuanId(res);
        pendingNewDiscussRef.current = savedId
          ? { kind: 'id', id: savedId }
          : { kind: 'newestUnderParent', parentId: parentIdSnapshot };
        setReplyContent('');
        setThaoLuanMention(null);
        setReplyFiles([]);
        setReplyParentId(null);
        const groupName = `congviec-thaoluan-${congViecChecklistId}`;
        try {
          await sendMessageToGroup(
            groupName,
            JSON.stringify({ type: 'thaoLuan', congViecId: congViecChecklistId, at: Date.now() })
          );
        } catch (err) {
          console.warn('broadcast group message failed', err);
        }
        await loadThaoLuan();
        message.success('Đã gửi phản hồi');
      } else {
        message.error('Gửi phản hồi không thành công');
      }
    } catch (error) {
      console.error('saveThaoLuan reply error', error);
      message.error('Có lỗi khi gửi phản hồi');
    } finally {
      setSendingReply(false);
    }
  };

  const scheduleChecklistRemove = (itemId: string) => {
    const key = String(itemId);
    const timeouts = checklistSyncTimeoutsRef.current;
    if (timeouts[key]) {
      window.clearTimeout(timeouts[key]);
      delete timeouts[key];
    }
    const isTemp = key.startsWith('temp');
    if (!congViecChecklistId || isTemp) return;
    window.setTimeout(async () => {
      try {
        const res: any = await congviecduanApi.removeChecklist({
          checklistId: key,
          ChecklistId: key,
          congViecID: congViecChecklistId,
          congViecId: congViecChecklistId,
          CongViecId: congViecChecklistId,
        });
        if (res?.success) {
          message.success('Đã xóa checklist');
        }
      } catch (error) {
        console.error('Checklist remove error', error);
        message.error('Không thể xóa checklist. Vui lòng thử lại.');
      }
    }, 0);
  };

  // Load checklist từ data
  useEffect(() => {
    if (discussionOnly) return;
    if (data) {
      const duAnCongViec = data?.duAnCongViec;
      const congViec = duAnCongViec?.congViec;
      const checklist = data?.listDuAnCongViecChecklist || [];
      if (Array.isArray(checklist) && checklist.length > 0) {
        setChecklistItems(
          checklist.map((item: any) => {
            const rawDate =
              item.dateTime ||
              item.ngayTao ||
              item.ngayChoice ||
              item.ngay ||
              item.createdDate ||
              item.createdAt ||
              '';
            let formattedDate = '';
            if (rawDate) {
              const parsed = dayjs(rawDate);
              formattedDate = parsed.isValid() ? parsed.format('DD/MM/YYYY') : String(rawDate);
            }
            return {
              id: item.id || item.checklistId || item.checklistID || CommonJs.createTempID(),
              ten: item.ten || item.tenChecklist || item.name || '',
              isCheck: item.isCheck || item.IsCheck || false,
              dateTime: formattedDate,
            };
          })
        );
      } else {
        setChecklistItems([]);
      }
      // Load hinhThucTongHop từ data
      const existingHinhThucTongHop =
        congViec?.hinhThucTongHop ?? duAnCongViec?.congViec?.hinhThucTongHop;
      const hasApiChilds = Array.isArray(duAnCongViec?.childs) && duAnCongViec.childs.length > 0;
      if (!hasApiChilds) {
        setHinhThucTongHop(1);
      } else if (existingHinhThucTongHop !== undefined) {
        setHinhThucTongHop(existingHinhThucTongHop);
      } else {
        setHinhThucTongHop(0);
      }

      // Load chi tiêu data từ congViec
      const chiTieu = congViec?.chiTieu ?? duAnCongViec?.congViec?.chiTieu ?? 0;
      const idDoLuong = congViec?.idDoLuong ??
        duAnCongViec?.congViec?.idDoLuong ??
        '';
      const tanSuat = congViec?.tanSuat ?? duAnCongViec?.congViec?.tanSuat ?? 1;

      setChiTieuValue(chiTieu);
      setIdDoLuongValue(idDoLuong);
      setTanSuatValue(tanSuat);
      savedTanSuatRef.current = tanSuat;
      const listCt = duAnCongViec?.listChiTieu;
      setListChiTieuDraft(
        Array.isArray(listCt) ? listCt.map((x: DuAnCongViecChiTieuItem) => ({ ...x })) : []
      );
      setHinhThucChiaChiTieu(congViec?.hinhThuc ?? 0);
    } else {
      // Nếu không có data, set default dựa trên childs
      const hasChilds = childs && childs.length > 0;
      setHinhThucTongHop(hasChilds ? 0 : 1);
      // Set default cho chi tiêu
      setChiTieuValue(0);
      setIdDoLuongValue('');
      setTanSuatValue(1);
      savedTanSuatRef.current = undefined;
      setListChiTieuDraft([]);
      setHinhThucChiaChiTieu(0);
    }
  }, [data, childs, discussionOnly]);

  const needsChiaLaiTanSuat = useMemo(() => {
    const saved = savedTanSuatRef.current;
    return (
      saved !== undefined &&
      saved !== null &&
      Number(tanSuatValue) !== Number(saved)
    );
  }, [tanSuatValue, data]);

  // Hàm check xem item có phải là mới không (id bắt đầu "temp")
  const isNewItem = (id: number | string): boolean => {
    if (typeof id === 'string' && id.startsWith('temp')) return true;
    return false;
  };

  const handleDeleteColumn = (id: number | string) => {
    // Chỉ cho phép xóa item mới
    if (!isNewItem(id)) return;
    const newColumns = childsColumns.filter((col) => col.id !== id);
    setChildsColumns(newColumns);
    // Sync với form
    if (form) {
      form.setFieldValue('childsColumns', newColumns);
    }
  };

  const handleAddColumn = () => {
    // Lấy người phụ trách và datetime của parent làm mặc định
    const parentOwnerId =
      firstPhuTrachIdForChildDefault(form?.getFieldValue('nv')) ||
      firstPhuTrachIdForChildDefault(data?.duAnCongViec?.congViec?.nguoiPhuTrachID) ||
      '';
    const parentDatetime = form?.getFieldValue('datetime');
    let parentDateStr = '';
    if (parentDatetime && Array.isArray(parentDatetime) && parentDatetime[0]) {
      const start = dayjs(parentDatetime[0]).format('DD/MM/YYYY');
      const end = parentDatetime[1] ? dayjs(parentDatetime[1]).format('DD/MM/YYYY') : null;
      parentDateStr = end ? `${start} - ${end}` : start;
    }

    // Sử dụng createNewChildColumn từ defaultModels
    const newCol: ColumnItem = createNewChildColumn(
      'Tên công việc',
      parentOwnerId,
      parentDateStr || dayjs().format('DD/MM/YYYY')
    );
    lastAddedChildIdRef.current = newCol.id;
    const newColumns = [...childsColumns, newCol];
    setChildsColumns(newColumns);
    // Sync với form
    if (form) {
      form.setFieldValue('childsColumns', newColumns);
      form.setFieldValue(`childsColumns_${newCol.id}`, parentOwnerId);
    }
  };

  // Sync form cho dòng con mới (chỉ temp)
  useEffect(() => {
    if (discussionOnly) return;
    if (!form) return;
    const parentNv = form.getFieldValue('nv') ?? data?.duAnCongViec?.congViec?.nguoiPhuTrachID;
    const patch: Record<string, string> = {};
    childsColumns.forEach((col) => {
      if (!isNewItem(col.id)) return;
      patch[`childsColumns_${col.id}`] =
        col.person || firstPhuTrachIdForChildDefault(parentNv) || '';
    });
    form.setFieldValue('childsColumns', childsColumns);
    if (Object.keys(patch).length > 0) {
      form.setFieldsValue(patch);
    }
  }, [childsColumns, form, data?.duAnCongViec?.congViec?.nguoiPhuTrachID, discussionOnly]);

  useEffect(() => {
    const addedId = lastAddedChildIdRef.current;
    if (addedId == null) return;

    requestAnimationFrame(() => {
      const container = childTableScrollRef.current;
      const row = container?.querySelector(`[data-child-row-id="${addedId}"]`) as HTMLElement | null;
      if (row) {
        row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }
      lastAddedChildIdRef.current = null;
    });
  }, [childsColumns]);

  const syncFormBodyScrollShadow = useCallback(() => {
    const el = formScrollPaneRef.current;
    if (!el) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    setFormBodyScrolled(scrollTop > 4);
    setFormBodyAtBottom(scrollTop + clientHeight >= scrollHeight - 4);
  }, []);

  useEffect(() => {
    syncFormBodyScrollShadow();
    const el = formScrollPaneRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => syncFormBodyScrollShadow());
    ro.observe(el);
    return () => ro.disconnect();
  }, [syncFormBodyScrollShadow, discussItems.length, checklistItems.length, childsColumns.length, fileAttachCount]);

  // Handle save
  const handleSave = async () => {
    if (!form || !idDuAn || idDuAn === 'undefined') {
      message.warning('Vui lòng chọn dự án');
      return;
    }

    setSaving(true);
    try {
      const values = form.getFieldsValue();
      // Lấy childsColumns trực tiếp từ columns state (source of truth) thay vì form values
      // vì columns state được cập nhật ngay khi user thêm/sửa/xóa child

      // Get parent congViec from data
      const duAnCongViec = data?.duAnCongViec;
      const parentCongViec = duAnCongViec?.congViec;
      if (!parentCongViec) {
        message.error('Không tìm thấy thông tin công việc');
        return;
      }

      // Parse datetime
      const datetime = values.datetime;
      const ngayBatDau = datetime && datetime[0] ? validateAndConvertDate(datetime[0]) || undefined : undefined;
      const ngayKetThuc = datetime && datetime[1] ? validateAndConvertDate(datetime[1]) || undefined : undefined;

      // Parse nguoiHoTroID
      const nguoiHoTroID = Array.isArray(values.nvSupport) ? values.nvSupport.join(',') : values.nvSupport || '';
      const filesDinhKemPayload = dedupeFileLinks(
        parseFileLinks(form.getFieldValue('filesDinhKem') ?? values.filesDinhKem)
      ).join(',');

      // Ngân sách từ form (unit) -> number, ưu tiên state local rồi tới form, cuối cùng fallback parent
      let unitValue = parentCongViec.nganSach ?? parentCongViec.nganSachCal ?? 0;
      if (unitValueState !== undefined && unitValueState !== null) {
        unitValue = unitValueState;
      } else if (values.unit !== undefined && values.unit !== null && values.unit !== '') {
        const parsed = Number(values.unit);
        if (!Number.isNaN(parsed)) {
          unitValue = parsed;
        }
      }
      // Update parent congViec
      const congViecEdit: DuAnCongViecDTO = {
        ...parentCongViec,
        parentID: parentCongViec.parentID || '',
        tenCongViec: values.projectName || parentCongViec.tenCongViec,
        nguoiPhuTrachID:
          joinNguoiPhuTrachFormValue(values.nv) || parentCongViec.nguoiPhuTrachID,
        nguoiHoTroID: nguoiHoTroID,
        ...(ngayBatDau && { ngayBatDau }),
        ...(ngayKetThuc && { ngayKetThuc }),
        nganSach: unitValue,
        mtctid: values.idMucTieu || parentCongViec.mtctid || '',
        viTri:
          values.viTri !== undefined
            ? Number(values.viTri)
            : fullCheckList.find((x) => x.id === currentCongViecId)?.viTri ??
            parentCongViec.viTri ??
            0,
        // tiTrong / tiTrongCal giữ từ parentCongViec (chỉ hiển thị trong form, không chỉnh ở modal)
        moTa: values.description ?? '',
        kanBanMucDoUuTien: values.kanBanMucDoUuTien || parentCongViec.kanBanMucDoUuTien || 'LOW',
        filesDinhKem: filesDinhKemPayload || parentCongViec.filesDinhKem || '',
        // Giữ nguyên kanban data
        kanBanViTri: parentCongViec.kanBanViTri ?? 0,
        kanbans: parentCongViec.kanbans || [],
        hinhThucTongHop: hinhThucTongHop,
        chiTieu: chiTieuValue,
        idDoLuong: idDoLuongValue,
        IDDoLuong: idDoLuongValue, // Giữ cả 2 để tương thích
        tanSuat: tanSuatValue,
        hinhThuc: hinhThucChiaChiTieu,
        hasChange: true,
        ngayBatDau:
          ngayBatDau ||
          (parentCongViec.ngayBatDau
            ? toActionPlanCalendarDate(parentCongViec.ngayBatDau) ?? undefined
            : undefined),
        ngayKetThuc:
          ngayKetThuc ||
          (parentCongViec.ngayKetThuc
            ? toActionPlanCalendarDate(parentCongViec.ngayKetThuc) ?? undefined
            : undefined),
        minDateChild: ngayBatDau
          ? dayjs(ngayBatDau).format('YYYY-MM-DD')
          : dayjs().format('YYYY-MM-DD'),
        maxDateChild: ngayKetThuc
          ? dayjs(ngayKetThuc).format('YYYY-MM-DD')
          : dayjs().format('YYYY-MM-DD'),
        hienThiTrangQuanLy:
          values.hienThiTrangQuanLy ??
          (String(parentCongViec.id || '').startsWith('temp')
            ? true
            : (parentCongViec.hienThiTrangQuanLy ?? false)),
      } as DuAnCongViecDTO;

      // Convert childsColumns mới  to DuAnCongViecTree[]
      const allChilds = convertChildsColumnsToDuAnCongViecTree(childsColumns, childs, congViecEdit as DuAnCongViecDTO, false);

      // Build DuAnCongViecTree for parent, giữ nguyên các field không đổi từ data.duAnCongViec / DuAnView
      const originalTree: DuAnCongViecTree | undefined = data?.duAnCongViec
      const baseTrongKy = originalTree?.trongKy
      const baseLuyKe = originalTree?.luyke
      const baseRoleItemTree = originalTree?.roleItemTree
      const normalizedTrongKy = {
        ...baseTrongKy,
        icon: (baseTrongKy as any)?.icon ?? (baseTrongKy as any)?.Icon ?? '',
        giaiDoan: (baseTrongKy as any)?.giaiDoan ?? (baseTrongKy as any)?.GiaiDoan ?? '',
        titleIcon: (baseTrongKy as any)?.titleIcon ?? (baseTrongKy as any)?.TitleIcon ?? '',
      };

      // Thời gian hiển thị (ThoiGian) cho DuAnView / ListCongViecDuAn[0]
      const thoiGianStr =
        datetime && Array.isArray(datetime) && datetime[0]
          ? `${dayjs(datetime[0]).format('DD/MM/YYYY')} - ${dayjs(
            datetime[1] ? datetime[1] : datetime[0]
          ).format('DD/MM/YYYY')}`
          : originalTree?.thoiGian || '';

      // Tên tần suất & hình thức tính (TenTanSuat, TenHinhThucTinh) – giữ nguyên nếu có, fallback từ giá trị hiện tại
      const tenTanSuat =
        originalTree?.tenTanSuat ||
        (() => {
          switch (tanSuatValue || parentCongViec.tanSuat) {
            case 1:
              return 'Ngày';
            case 2:
              return 'Tuần';
            case 3:
              return 'Tháng';
            case 4:
              return 'Cuối kỳ';
            default:
              return '';
          }
        })();

      const tenHinhThucTinh =
        originalTree?.tenHinhThucTinh ||
        (() => {
          switch (hinhThucTongHop) {
            case 0:
              return 'Hiệu suất tỉ trọng';
            case 1:
              return 'Báo cáo nhập tay';
            case 2:
              return 'Tổng con';
            case 3:
              return 'Trung bình con';
            default:
              return '';
          }
        })();

      const duAnViewTree: DuAnCongViecTree = {
        ...(originalTree as any),
        congViec: congViecEdit,
        hasChange: true,
        hasChild: allChilds.length > 0,
        idTemp: originalTree?.idTemp || '',
        param: originalTree?.param || '',
        childs: allChilds,
        countChilds: allChilds.length,
        thoiGian: thoiGianStr,
        tenTanSuat,
        tenHinhThucTinh,
        trongKy: {
          ...normalizedTrongKy,
          hoanThanh: progress || 0,
          // Giữ song song key PascalCase để đáp ứng model binder/validator BE.
          Icon: normalizedTrongKy.icon,
          GiaiDoan: normalizedTrongKy.giaiDoan,
          TitleIcon: normalizedTrongKy.titleIcon,
        },
        luyke: baseLuyKe,
        roleItemTree: {
          ...baseRoleItemTree,
          hasChild: allChilds.length > 0,
          isRoot: true,
        },
        listChiTieu: normalizeListChiTieuNgayForApi(
          needsChiaLaiTanSuat
            ? listChiTieuDraft // chỉ draft sau chia lại (đã clear khi đổi tần suất)
            : listChiTieuDraft.length > 0
              ? listChiTieuDraft
              : (originalTree?.listChiTieu ?? [])
        ) as DuAnCongViecChiTieuItem[],
      };

      const editRequest: EditCongViecsRequest = {
        PhanHe: 'bsc',
        Data: {
          DuAnID: idDuAn,
          ListCongViecDuAn: [duAnViewTree],
          AvatarPhuTrach: '',
          DuAnView: duAnViewTree,
          ListCongViecDuAnCheck:
            fullCheckList.length > 0 ? fullCheckList : data?.listCongViecDuAnCheck ?? [],
          tabCurrent: 'chitietcongviec',
        },
      };
      // return
      // Call API
      const response: any = await congviecduanApi.editcongviecs(editRequest);
      if (response.success) {
        message.success('Lưu thành công');
        onSave?.(response?.data);
      } else {
        message.error('Lưu thất bại');
      }
    } catch (error) {
      console.error('Error saving:', error);
      message.error('Có lỗi xảy ra khi lưu');
    } finally {
      setSaving(false);
    }
  };

  // Expose handleSave and saving state via ref
  useImperativeHandle(ref, () => ({
    handleSave,
    saving,
  }));

  const thaoLuanMentionPortal =
    typeof document !== 'undefined' &&
      thaoLuanMention &&
      thaoLuanMentionFiltered.length > 0 &&
      thaoLuanMentionMenuPos &&
      thaoLuanMentionListboxStyle
      ? createPortal(
        <div role="listbox" style={thaoLuanMentionListboxStyle}>
          {thaoLuanMentionFiltered.map((row, idx) => (
            <div
              key={`${row.key}-${idx}`}
              role="option"
              aria-selected={idx === thaoLuanMentionIndex}
              onMouseEnter={() => onThaoLuanMentionItemMouseEnter(idx)}
              onMouseDown={(ev) => onThaoLuanMentionItemMouseDown(ev, row)}
              style={{
                ...thaoLuanMentionOptionBaseStyle,
                background: idx === thaoLuanMentionIndex ? '#1677ff' : '#fff',
                color: idx === thaoLuanMentionIndex ? '#fff' : '#111',
              }}
            >
              {row.label}
            </div>
          ))}
        </div>,
        document.body
      )
      : null;

  const thaoLuanReplyComposer = (item: DiscussItem) => (
    <div className="cvda-discuss-composer mt-1 mb-1">
      <input
        ref={replyFileInputRef}
        type="file"
        multiple
        onChange={handleReplyFilesChange}
        style={{ display: 'none' }}
      />
      <Input.TextArea
        ref={replyTextAreaRef}
        rows={2}
        value={replyContent}
        onChange={(e) => {
          const el = e.currentTarget;
          setReplyContent(el.value);
          syncThaoLuanMentionFromTextareaEl(el, 'reply');
        }}
        onClick={(e) => syncThaoLuanMentionFromTextareaEl(e.currentTarget, 'reply')}
        onKeyUp={(e) => syncThaoLuanMentionFromTextareaEl(e.currentTarget, 'reply')}
        onKeyDown={(e) =>
          handleThaoLuanMentionListKeyDown(e, 'reply', replyContent, replyTextAreaRef)
        }
        placeholder={` Đang trả lời ${resolveDiscussAuthorLabel(item, replyMentionOptionRows)}`}
        maxLength={500}
        showCount={false}
      />
      <div className="cvda-discuss-composer-toolbar">
        <Button
          type="default"
          size="small"
          icon={<PaperClipOutlined />}
          onClick={() => replyFileInputRef.current?.click()}
          shape="default"
        />
        <div className="flex items-center gap-2">
          <Button
            type="default"
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => {
              setReplyParentId(null);
              setReplyContent('');
              setReplyFiles([]);
              setThaoLuanMention(null);
            }}
          >
            Hủy
          </Button>
          <Button
            type="primary"
            size="small"
            loading={sendingReply}
            icon={<SendOutlined />}
            shape="default"
            onClick={handleSendReply}
          >
            Gửi
          </Button>
        </div>
      </div>
      {replyFiles.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {replyFiles.map((f, idx) => (
            <span
              key={`${f.name}-${idx}`}
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-white border border-gray-300 text-xs text-gray-700"
            >
              <PaperClipOutlined style={{ fontSize: 10, marginRight: 4 }} />
              <span className="max-w-[140px] truncate">{f.name}</span>
              <button
                type="button"
                className="ml-1 text-[10px] text-gray-400 hover:text-red-500"
                onClick={() =>
                  setReplyFiles((prev) => prev.filter((_, fileIdx) => fileIdx !== idx))
                }
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );

  const thaoLuanDiscussList = (
    <div className="w-full ">
      <div className="cvda-detail-form-section-header">
        <span className="cvda-detail-form-section-title">
          <Space size={6} align="center">
            Thảo luận
            {discussItems.length > 0 && (
              <Badge count={discussItems.length} showZero color="blue" size="small" />
            )}
          </Space>
        </span>
      </div>
      <div className="cvda-detail-form-section-card cvda-detail-form-discuss-content ">
        {discussItems.length > 0 ? (
          <DiscussComponent
            data={discussItems}
            thaoLuanMentionFiltered={replyMentionOptionRows}
            highlightDiscussId={highlightDiscussId}
            activeReplyId={replyParentId}
            onReplyClick={(item) => {
              setReplyParentId(String(item.id));
              setReplyContent('');
              setReplyFiles([]);
              setThaoLuanMention(null);
            }}
            renderReplyComposer={thaoLuanReplyComposer}
          />
        ) : (
          <div className="cvda-detail-form-section-empty">Chưa có thảo luận</div>
        )}
      </div>
    </div>
  );

  const thaoLuanComposer = (
    <div className="cvda-detail-form-discuss-composer-wrap">
      <div className="cvda-discuss-composer">
        <input
          ref={discussFileInputRef}
          type="file"
          multiple
          onChange={handleDiscussFilesChange}
          style={{ display: 'none', border: 'none' }}
        />
        <Input.TextArea
          ref={discussTextAreaRef}
          rows={1}
          value={discussContent}
          onChange={(e) => {
            const el = e.currentTarget;
            setDiscussContent(el.value);
            syncThaoLuanMentionFromTextareaEl(el, 'discuss');
          }}
          onClick={(e) => syncThaoLuanMentionFromTextareaEl(e.currentTarget, 'discuss')}
          onKeyUp={(e) => syncThaoLuanMentionFromTextareaEl(e.currentTarget, 'discuss')}
          onKeyDown={(e) =>
            handleThaoLuanMentionListKeyDown(e, 'discuss', discussContent, discussTextAreaRef)
          }
          placeholder="Nhập nội dung thảo luận..."
          maxLength={500}
        />
        <div className="cvda-discuss-composer-toolbar">
          <Button
            type="default"
            size="middle"
            icon={<PaperClipOutlined />}
            onClick={() => discussFileInputRef.current?.click()}
            shape="default"
          />
          <Button
            type="primary"
            size="middle"
            loading={sendingDiscuss}
            icon={<SendOutlined />}
            shape="default"
            onClick={handleSendDiscussion}
          >Gửi
          </Button>
        </div>
      </div>
      {discussFiles.length > 0 && (
        <div className="cvda-detail-form-discuss-files">
          {discussFiles.map((f, idx) => (
            <span key={`${f.name}-${idx}`} className="cvda-detail-form-discuss-file-chip">
              <PaperClipOutlined style={{ fontSize: 10, marginRight: 4 }} />
              <span className="cvda-detail-form-discuss-file-chip-name">{f.name}</span>
              <button
                type="button"
                className="ml-1 text-[10px] text-gray-400 hover:text-red-500"
                onClick={() =>
                  setDiscussFiles((prev) => prev.filter((_, fileIdx) => fileIdx !== idx))
                }
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );

  const thaoLuanSection = (
    <div className="w-full ">
      {thaoLuanDiscussList}
      <div className="mt-2">{thaoLuanComposer}</div>
    </div>
  );

  if (discussionOnly) {
    return (
      <>
        <div
          className="flex flex-col w-full h-full overflow-y-auto p-3 sm:p-4"
          style={{ minHeight: '100%' }}
        >
          {thaoLuanSection}
        </div>
        {thaoLuanMentionPortal}
      </>
    );
  }

  return (
    <>
      <div className="cvda-detail-form-layout w-full">
        <div className="cvda-detail-form-card">
          {/* Tên dự án */}
          <FormRow>
            <FileTextOutlined className="cvda-detail-form-icon" />
            <FormLabel>Tên</FormLabel>
            <FormField>
              <Form.Item label="" name="projectName" className="!mb-0">
                <Input
                  name="projectName"
                  defaultValue="Tên dự án"
                  className="project-name-input"
                />
              </Form.Item>
            </FormField>
          </FormRow>

          {/* Tiến độ + Thời gian + Ngân sách + Ưu tiên + Vị trí + Hiển thị */}
          <FormRow>
            <DashboardOutlined className="cvda-detail-form-icon" />
            <FormLabel>Tiến độ</FormLabel>
            <FormField className="cvda-detail-form-metrics-inline">
              <div className="cvda-detail-form-metric">
                {isEdit && !((childsColumns?.length ?? 0) > 0) ? (
                  <CustomTooltip
                    value="Nhấn vào để chia chỉ tiêu"
                  >
                    <Form.Item name="progress" style={{ marginBottom: 0 }}>
                      <Popover
                        content={
                          <div style={{ width: 520, padding: '0px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                              <thead>
                                <tr>
                                  <th style={{ textAlign: 'center', fontWeight: 'bold', width: '22%', padding: '10px 8px', borderBottom: '1px solid #e8e8e8', fontSize: 13, color: '#595959', whiteSpace: 'nowrap' }}>
                                    Chỉ tiêu
                                  </th>
                                  <th style={{ textAlign: 'center', fontWeight: 'bold', width: '20%', padding: '10px 8px', borderBottom: '1px solid #e8e8e8', fontSize: 13, color: '#595959', whiteSpace: 'nowrap' }}>
                                    Đơn vị
                                  </th>
                                  <th style={{ textAlign: 'center', fontWeight: 'bold', width: '25%', padding: '10px 8px', borderBottom: '1px solid #e8e8e8', fontSize: 13, color: '#595959', whiteSpace: 'nowrap' }}>
                                    Báo cáo
                                  </th>
                                  <th style={{ textAlign: 'center', fontWeight: 'bold', width: '10%', padding: '10px 8px', borderBottom: '1px solid #e8e8e8', fontSize: 13, color: '#595959', whiteSpace: 'nowrap' }}>

                                  </th>

                                  <th style={{ textAlign: 'center', fontWeight: 'bold', width: '33%', padding: '10px 8px', borderBottom: '1px solid #e8e8e8', fontSize: 13, color: '#595959', whiteSpace: 'nowrap' }}>
                                    Hình thức tổng hợp
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td style={{ textAlign: 'right', padding: '10px 8px', verticalAlign: 'middle' }}>
                                    <InputNumber
                                      value={chiTieuValue}
                                      onChange={(val) => setChiTieuValue(val || 0)}
                                      style={{ width: '100%', textAlign: 'right' }}
                                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                      parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, '')) || 0}
                                      controls={false}
                                      bordered={true}
                                    />
                                  </td>
                                  <td style={{ padding: '10px 8px', verticalAlign: 'middle' }}>
                                    <Select
                                      popupMatchSelectWidth={false}
                                      value={idDoLuongValue || undefined}
                                      onChange={(val) => setIdDoLuongValue(val)}
                                      style={{ width: '100%' }}
                                      placeholder="Chọn đơn vị"
                                      showSearch
                                      filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                      }
                                      options={
                                        unitHelpers?.transformUnitsToOptions
                                          ? unitHelpers
                                            .transformUnitsToOptions()
                                            .filter((opt) => !unitHelpers?.isThangDoUnit(String(opt.value)))
                                          : []
                                      }
                                      bordered={false}
                                    />
                                  </td>
                                  <td style={{ padding: '10px 8px', verticalAlign: 'middle' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <Select

                                        popupMatchSelectWidth={false}
                                        value={tanSuatValue}
                                        onChange={(val) => {
                                          setTanSuatValue(val);
                                          // Đổi tần suất → bỏ draft kỳ cũ; chia lại trước khi lưu.
                                          setListChiTieuDraft([]);
                                        }}
                                        style={{ flex: 1, width: '100%' }}
                                        options={[
                                          { label: 'Ngày', value: 1 },
                                          { label: 'Tuần', value: 2 },
                                          { label: 'Tháng', value: 3 },
                                          { label: 'Báo cáo cuối kỳ', value: 4 },
                                        ]}
                                        bordered={false}
                                      />

                                    </div>

                                  </td>
                                  <td style={{ textAlign: 'center', padding: '10px 8px', verticalAlign: 'middle' }}>
                                    <Tooltip
                                      title={
                                        needsChiaLaiTanSuat
                                          ? 'Đã đổi tần suất — bấm để chia lại chỉ tiêu và lưu (API)'
                                          : 'Chia chỉ tiêu theo tần suất'
                                      }
                                    >
                                      <span
                                        role="button"
                                        tabIndex={0}
                                        className="inline-flex items-center leading-none"
                                        style={{ cursor: 'pointer' }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setChiaChiTieuModalVisible(true);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setChiaChiTieuModalVisible(true);
                                          }
                                        }}
                                      >
                                        {needsChiaLaiTanSuat ? (
                                          <WarningFilled style={{ fontSize: 16, color: 'red', cursor: 'pointer', padding: '4px' }} />
                                        ) : (
                                          <DownCircleOutlined
                                            style={{ fontSize: 18, color: '#ed7d31', cursor: 'pointer', padding: '4px' }}
                                          />
                                        )}
                                      </span>
                                    </Tooltip>
                                  </td>
                                  <td style={{ padding: '10px 8px', verticalAlign: 'middle' }}>
                                    <Select
                                      value={hinhThucTongHop}
                                      onChange={(val) => setHinhThucTongHop(val)}
                                                                          style={{ width: '100%' }}
                                                                          options={hinhThucTongHopOptions}
                                      bordered={false}
                                    />
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        }
                        trigger="click"
                        open={chiTieuPopoverVisible}
                        onOpenChange={setChiTieuPopoverVisible}
                        placement="bottomLeft"
                      >
                        <div style={{ cursor: 'pointer' }}>
                          <div className="flex items-center gap-x-1" style={{ justifyContent: 'flex-start', width: '100%' }}>

                            <Progress
                              type="circle"
                              percent={Math.max(0, Math.min(Number(progress) || 0, 100))}
                              size={28}
                              strokeColor="#ffab00"
                              style={{ flexShrink: 0 }}
                              format={() => ''}
                            />
                            <span style={{ color: '#000', fontSize: 11, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                              {CommonJs.formatPercentNumber(Math.max(0, Math.min(Number(progress) || 0, 100)))}%
                            </span>
                          </div>
                        </div>
                      </Popover>
                    </Form.Item>
                  </CustomTooltip>
                ) : (
                  <CustomTooltip
                    value=""
                  >
                    <div className="flex items-center gap-x-1" style={{ justifyContent: 'flex-start', width: '100%', cursor: 'pointer' }}>
                      <Progress
                        type="circle"
                        percent={Math.max(0, Math.min(Number(progress) || 0, 100))}
                        size={28}
                        strokeColor="#ffab00"
                        style={{ flexShrink: 0 }}
                        format={() => ''}
                      />
                      <span style={{ color: '#000', fontSize: 11, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                        {CommonJs.formatPercentNumber(Math.max(0, Math.min(Number(progress) || 0, 100)))}%
                      </span>
                    </div>
                  </CustomTooltip>
                )}

              </div>

              {/* Thời gian */}
              <div className="cvda-detail-form-metric">
                <Form.Item label="" name="datetime" className="!mb-0">
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => prevValues.datetime !== currentValues.datetime}
                  >
                    {({ getFieldValue, setFieldValue }) => {
                      const datetimeValue = getFieldValue('datetime');

                      let minChildStart: dayjs.Dayjs | null = null;
                      let maxChildEnd: dayjs.Dayjs | null = null;

                      if (Array.isArray(childs) && childs.length > 0) {
                        childs.forEach((child) => {
                          const congViec = child.congViec;
                          if (congViec.ngayBatDau) {
                            const s = dayjs(congViec.ngayBatDau);
                            if (!minChildStart || s.isBefore(minChildStart, 'day')) {
                              minChildStart = s;
                            }
                          }
                          if (congViec.ngayKetThuc) {
                            const e = dayjs(congViec.ngayKetThuc);
                            if (!maxChildEnd || e.isAfter(maxChildEnd, 'day')) {
                              maxChildEnd = e;
                            }
                          }
                        });
                      }

                      if (Array.isArray(childsColumns) && childsColumns.length > 0) {
                        childsColumns.forEach((col) => {
                          if (col.dateTime) {
                            if (col.dateTime.includes(' - ')) {
                              const [startStr, endStr] = col.dateTime.split(' - ');
                              const start = dayjs(startStr, 'DD/MM/YYYY');
                              const end = dayjs(endStr, 'DD/MM/YYYY');
                              if (start.isValid()) {
                                if (!minChildStart || start.isBefore(minChildStart, 'day')) {
                                  minChildStart = start;
                                }
                              }
                              if (end.isValid()) {
                                if (!maxChildEnd || end.isAfter(maxChildEnd, 'day')) {
                                  maxChildEnd = end;
                                }
                              }
                            } else {
                              const date = dayjs(col.dateTime, 'DD/MM/YYYY');
                              if (date.isValid()) {
                                if (!minChildStart || date.isBefore(minChildStart, 'day')) {
                                  minChildStart = date;
                                }
                                if (!maxChildEnd || date.isAfter(maxChildEnd, 'day')) {
                                  maxChildEnd = date;
                                }
                              }
                            }
                          }
                        });
                      }

                      const parentRange =
                        parentCongViec?.ngayBatDau && parentCongViec?.ngayKetThuc
                          ? {
                            start: dayjs(parentCongViec.ngayBatDau),
                            end: dayjs(parentCongViec.ngayKetThuc),
                          }
                          : null;
                      const initialRange: [dayjs.Dayjs, dayjs.Dayjs] | null =
                        datetimeValue && Array.isArray(datetimeValue) && datetimeValue[0]
                          ? [dayjs(datetimeValue[0]), datetimeValue[1] ? dayjs(datetimeValue[1]) : dayjs(datetimeValue[0])]
                          : null;
                      let year = new Date().getFullYear();
                      if (datetimeValue && Array.isArray(datetimeValue) && datetimeValue[0]) {
                        year = dayjs(datetimeValue[0]).year();
                      } else if (parentRange?.start) {
                        year = parentRange.start.year();
                      }
                      return (
                        <DuAnCongViecDatetimePicker
                          value={datetimeValue || [null, null]}
                          onChange={(dates: any) => {
                            setFieldValue('datetime', dates);
                          }}
                          parentRange={parentRange}
                          initialRange={initialRange}
                          minStartAllowed={minChildStart}
                          maxEndAllowed={maxChildEnd}
                          year={year}
                          style={{ border: 'none', boxShadow: 'none', width: 'auto' }}
                        />
                      );
                    }}
                  </Form.Item>
                </Form.Item>
              </div>

              {/* Tổng ngân sách */}
              <div className="cvda-detail-form-metric">
                <img
                  src={(BudgetIcon as any).src || (BudgetIcon as any)}
                  alt="Ngân sách"
                  className="cvda-detail-form-icon"
                />
                <div className="flex items-center gap-1 min-w-0 max-w-[100px]">
                  {!isEditingBudget ? (
                    <div className="flex items-center gap-2 cursor-pointer " onClick={() => setIsEditingBudget(true)}>
                      <Tooltip title={`${nganSachToolTip}`} >
                        <div style={{ position: 'relative', width: 100, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                          <Progress
                            percent={Number(phanTramNganSach)}
                            percentPosition={{ align: 'center', type: 'inner' }}
                            size={[100, 20]}
                            strokeColor="#ffab00"
                            style={{ flex: 1, maxWidth: '80px', fontWeight: 'bold' }}
                            format={() => <span style={{ color: '#000', fontWeight: 'bold', fontSize: 11 }}>{CommonJs.formatPercentNumber(Number(phanTramNganSach) || 0)}%</span>}
                          />

                        </div>
                      </Tooltip>
                    </div>
                  ) : (
                    <div className="cvda-ngan-sach-cell">
                    
                      <FormattedDecimalInput
                        autoFocus
                        value={unitFormValue}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        }}
                        onValueCommit={(numValue) => {
                          const value = numValue ?? 0;
                          setUnitValueState(value);
                          form?.setFieldValue('unit', value);
                          setIsEditingBudget(false);
                        }}
                        className="text-right cvda-ngan-sach-input"
                        style={{
                          textAlign: 'right',
                          border: 'none',
                          backgroundColor: 'transparent',
                          boxShadow: 'none',
                          outline: 'none',
                          fontSize: 14,
                          margin: 0,
                        }}
                      />
                      <span style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                        {NGANSACH_UNIT}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              {/* Độ ưu tiên */}
              <div className="cvda-detail-form-metric">
                <Form.Item
                  name="kanBanMucDoUuTien"
                  noStyle
                  normalize={(value) => (value ? String(value).toUpperCase() : 'LOW')}
                >
                  <Select
                    popupMatchSelectWidth={false}
                    suffixIcon={null}
                    bordered={false}
                    className="cvda-detail-form-input-plain min-w-[40px] text-sm font-bold !border-0 !shadow-none"
                    options={[
                      {
                        value: 'LOW',
                        label: (
                          <span className="px-2 py-[2px] rounded-full bg-[#34c759] text-white font-semibold inline-flex items-center text-xs">
                            LOW
                          </span>
                        ),
                      },
                      {
                        value: 'MEDIUM',
                        label: (
                          <span className="px-2 py-[2px] rounded-full bg-[#f5a623] text-white font-semibold inline-flex items-center text-xs">
                            MEDIUM
                          </span>
                        ),
                      },
                      {
                        value: 'HIGH',
                        label: (
                          <span className="px-2 py-[2px] rounded-full bg-[#ff4d4f] text-white font-semibold inline-flex items-center text-xs">
                            HIGH
                          </span>
                        ),
                      },
                    ]}
                    optionLabelProp="label"
                  />
                </Form.Item>
              </div>

              {/* Vị trí */}
              <div className="cvda-detail-form-metric">
                <span className="cvda-detail-form-metric-label is-fixed">Vị trí</span>
                <CustomTooltip value="Bấm để chỉnh sửa vị trí">
                  <Popover
                    trigger="click"
                    placement="bottomRight"
                    title="Sắp xếp vị trí"
                    content={
                      <div className="custom-scrollbar w-[300px]">
                        <ViTriSiblingDragList
                          items={siblingViTriItems}
                          currentId={currentCongViecId}
                          onChange={handleSiblingViTriChange}
                        />
                      </div>
                    }
                  >
                    <Button
                      type="primary"
                      size="small"
                      className="!text-black !bg-white !shadow-none text-sm"
                    >
                      {siblingViTriItems.find((x) => x.id === currentCongViecId)?.viTri ??
                        form?.getFieldValue('viTri') ??
                        data?.duAnCongViec?.congViec?.viTri ??
                        0}
                    </Button>
                  </Popover>
                  <Form.Item name="viTri" hidden>
                    <Input />
                  </Form.Item>
                </CustomTooltip>
              </div>

              {/* Hiển thị trang quản lý */}
              <div className="cvda-detail-form-metric">
                <span className="cvda-detail-form-metric-label">Hiển thị</span>
                <Tooltip title="Bật tùy chọn này để công việc hiển thị ở trang Quản lý Action Plan Bộ phận.">
                  <InfoCircleOutlined className="text-[#6B7280] text-sm" />
                </Tooltip>
                <Form.Item name="hienThiTrangQuanLy" valuePropName="checked" noStyle>
                  <Checkbox />
                </Form.Item>
              </div>
            </FormField>
          </FormRow>

          {/* Tỉ trọng + Hình thức tổng hợp */}
          <FormRow>
            <PieChartOutlined className="cvda-detail-form-icon" />
            <FormLabel>Tỉ trọng</FormLabel>
            <FormField className="cvda-detail-form-secondary-inline">
              <div className="cvda-detail-form-titrong-inline">
                <Form.Item name="tiTrongHienThi" noStyle>
                  <Input
                    readOnly
                    type="number"
                    placeholder="Tỉ trọng"
                    className="cvda-detail-form-input-plain cvda-detail-form-titrong-value text-sm cursor-default bg-white"
                  />
                </Form.Item>
                <CustomTooltip value="Hình thức tổng hợp">
                <FormLabel>Hình thức tổng hợp</FormLabel>
                  <Select
                    value={hinhThucTongHop}
                    onChange={(value) => setHinhThucTongHop(value)}
                    className="cvda-detail-form-input-plain cvda-detail-form-hinh-thuc-select text-sm"
                    popupMatchSelectWidth={false}
                    bordered={false}
                    options={[
                      { label: 'Hiệu suất tỉ trọng', value: 0 },
                      { label: 'Báo cáo nhập tay', value: 1 },
                      { label: 'Tổng con', value: 2 },
                      { label: 'Trung bình con', value: 3 },
                    ]}
                  />
                </CustomTooltip>
              </div>
            </FormField>
          </FormRow>

          {/* Phụ trách / Hỗ trợ / Người tạo */}
          <FormRow>
            <UserOutlined className="cvda-detail-form-icon" />
            <FormLabel>Phụ trách</FormLabel>
            <FormField className="cvda-detail-form-assignee-inline">
              <div className="cvda-detail-form-assignee-group">
                <div className={ASSIGNEE_SELECT_BOX}>
                  <EmployeeAvatarSelect
                    name="nv"
                    form={form}
                    mode={isEdit ? 'single' : 'multiple'}
                    scrollable={!isEdit}
                    disabled={!isEdit}
                    isColumn={isColumn}
                    width="100%"
                    maxWidth="100%"
                    showName={false}
                    excludeIds={parseExcludeEmployeeIds(watchedNguoiHoTro)}
                  />
                </div>

                <div className="cvda-detail-form-assignee-segment">
                  <TeamOutlined className="cvda-detail-form-icon" />
                  <span className="cvda-detail-form-assignee-label">Hỗ trợ</span>
                  <div className={ASSIGNEE_SELECT_BOX}>
                    <EmployeeAvatarSelect
                      name="nvSupport"
                      form={form}
                      mode="multiple"
                      scrollable
                      isColumn={isColumn}
                      width="100%"
                      maxWidth="100%"
                      showName={false}
                      excludeIds={parseExcludeEmployeeIds(watchedNguoiPhuTrach)}
                    />
                  </div>
                </div>
                <div className="cvda-detail-form-assignee-segment is-compact">
                  <TeamOutlined className="cvda-detail-form-icon" />
                  <span className="cvda-detail-form-assignee-label">Người tạo</span>
                  <EmployeeAvatarSelect
                    name="nvNguoiTao"
                    mode="single"
                    isColumn={isColumn}
                    width={30}
                    maxWidth={30}
                    showName={false}
                    disabled
                  />
                </div>
              </div>


            </FormField>
          </FormRow>

          {/* Mục tiêu */}
          <FormRow>
            <AimOutlined className="cvda-detail-form-icon" />
            <FormLabel>Mục tiêu</FormLabel>
            <FormField className="cvda-detail-form-select-field">
              <Form.Item name="idMucTieu" noStyle className="w-full">
                <Select
                  disabled={isEdit}
                  popupMatchSelectWidth={false}
                  showSearch
                  placeholder="Mục tiêu"
                  optionFilterProp="label"
                  options={targetOptions}
                  allowClear
                  variant='borderless'
                  suffixIcon={null}
                  className="select-disabled-bold cvda-detail-form-input-plain w-full font-bold"
                />
              </Form.Item>
            </FormField>
          </FormRow>

          {/* Dự án */}
          {selectListDuAn && (
            <FormRow>
              <ProjectOutlined className="cvda-detail-form-icon" />
              <FormLabel>Dự án</FormLabel>
              <FormField className="cvda-detail-form-select-field">
                <Form.Item name="duAn" noStyle className="w-full">
                  <Select
                    disabled
                    className="select-disabled-bold cvda-detail-form-input-plain w-full text-sm font-bold"
                    popupMatchSelectWidth={false}
                    showSearch
                    placeholder="Dự án"
                    optionFilterProp="label"
                    options={
                      Array.isArray(selectListDuAn)
                        ? selectListDuAn.map((item: any) => ({ value: item.value, label: item.text }))
                        : selectListDuAn?.items?.map((item: any) => ({ value: item.value, label: item.text })) || []
                    }
                    variant='borderless'
                    suffixIcon={null}
                  />
                </Form.Item>
              </FormField>
            </FormRow>
          )}

        </div>
        <div
          ref={formScrollPaneRef}
          onScroll={syncFormBodyScrollShadow}
          className={`cvda-detail-form-body cvda-detail-form-scroll-pane custom-scrollbar${formBodyScrolled ? ' is-scrolled' : ''}`}
        >
          {/* Mô tả công việc */}
          <div className="cvda-detail-form-panel">
            <div className="cvda-detail-form-section-header">
              <span className="cvda-detail-form-section-title">Mô tả công việc</span>
            </div>
            <Form.Item name="description" className="!mb-0">
              {descriptionEditorKey ? (
                <CKEditorWrapperActionPlan
                  key={descriptionEditorKey}
                  type="inline"
                  data={descriptionValue}
                  onChange={(value: string) => {
                    form?.setFieldValue('description', value);
                  }}
                  onUploadImage={handleUploadImage}
                  resizable
                  minHeight={120}
                  maxHeight={480}
                  borderRadius={8}
                  borderInput="1px solid #e5e7eb"
                  className="cvda-detail-form-description-editor bg-white rounded-xl mt-1.5 "
                  placeholder="Nhập mô tả công việc"
                />
              ) : (
                <div
                  className="cvda-detail-form-description-editor cvda-detail-form-description-placeholder"
                  style={{ minHeight: 120 }}
                >
                  Nhập mô tả công việc
                </div>
              )}
            </Form.Item>
          </div>

          {/* Tài liệu đính kèm */}
          <div
            className={`cvda-detail-form-panel cvda-detail-form-file-drop-zone${isFileDragActive ? ' is-drag-active' : ''}`}
            onDragEnter={handleFileDragEnter}
            onDragOver={handleFileDragOver}
            onDragLeave={handleFileDragLeave}
            onDrop={handleFileDrop}
          >
            <div className="cvda-detail-form-section-header">
              <span className="cvda-detail-form-section-title">
                <Space size={6} align="center">
                  Tài liệu đính kèm
                  {fileAttachCount > 0 && <Badge count={fileAttachCount} showZero color="blue" size="small" />}
                </Space>
              </span>
              {isEdit && (
                <Form.Item shouldUpdate noStyle>
                  {({ getFieldValue }) => {
                    const currentRaw = form ? form.getFieldValue('filesDinhKem') : getFieldValue('filesDinhKem');
                    const files = parseFileLinks(currentRaw);
                    const setFiles = (next: string[]) => {
                      const safe = dedupeFileLinks(next.map((u) => String(u).trim()).filter(Boolean));
                      form?.setFieldValue('filesDinhKem', safe);
                    };
                    return (
                      <FileAndLinkUploadMenu
                        iconOnly
                        block={false}
                        urlModalZIndex={1200}
                        disabled={uploadingAttachments}
                        onAppendUrls={(urls) => setFiles([...files, ...urls])}
                        icon={<PaperClipOutlined />}
                      />
                    );
                  }}
                </Form.Item>
              )}

            </div>

            <div className="cvda-detail-form-section-card">
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => {
                  const currentRaw = form ? form.getFieldValue('filesDinhKem') : getFieldValue('filesDinhKem');
                  const files = parseFileLinks(currentRaw);

                  const setFiles = (next: string[]) => {
                    const safe = dedupeFileLinks(next.map((u) => String(u).trim()).filter(Boolean));
                    form?.setFieldValue('filesDinhKem', safe);
                  };

                  return (
                    <div>
                      {files.length === 0 ? (
                        <div className="cvda-detail-form-section-empty">Chưa có file đính kèm</div>
                      ) : (
                        <div className="cvda-detail-form-file-list">
                          {files.map((url, index) => {
                            const fileName = extractFileNameFromUrl(url) || 'Tài liệu';

                            return (
                              <div key={`${url}-${index}`} className="cvda-detail-form-section-row cvda-detail-form-file-item">
                                <span className="cvda-detail-form-file-name" title={fileName}>
                                  {fileName}
                                </span>

                                <div className="cvda-detail-form-file-actions">
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<EyeOutlined />}
                                    title="Xem trước"
                                    onClick={() => setPreviewFile({ fileName, filePath: url })}
                                  />
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<DownloadOutlined />}
                                    title="Tải xuống"
                                    onClick={() => CommonJs.downloadFile(url, fileName)}
                                  />
                                  {isEdit && (
                                    <Button
                                      type="text"
                                      size="small"
                                      danger
                                      icon={<DeleteOutlined />}
                                      title="Xóa"
                                      onClick={() => setFiles(files.filter((u) => u !== url))}
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                    </div>
                  );
                }}
              </Form.Item>
            </div>

            {/* Trạng thái upload khi kéo thả file */}
            {attachmentUploads.length > 0 && (
              <div className="cvda-detail-form-upload-list" aria-live="polite">
                {attachmentUploads.map((upload) => (
                  <div key={upload.clientId} className="cvda-detail-form-section-row cvda-detail-form-upload-item">
                    <span className="cvda-detail-form-file-name" title={upload.name}>
                      {upload.name}
                    </span>
                    {upload.status === 'error' ? (
                      <>
                        <span className="cvda-detail-form-upload-error" title={upload.error}>
                          Tải lên thất bại
                        </span>
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          title="Bỏ qua"
                          onClick={() => setAttachmentUploads((current) => removeUploadItem(current, upload.clientId))}
                        />
                      </>
                    ) : (
                      <span className="cvda-detail-form-upload-progress">
                        <Progress percent={Math.round(upload.progress)} size="small" showInfo={false} />
                        <span className="cvda-detail-form-upload-percent">{Math.round(upload.progress)}%</span>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Overlay khi kéo file qua khu vực đính kèm */}
            {isFileDragActive && (
              <div className="cvda-detail-form-file-drop-overlay">
                <PaperClipOutlined />
                <span>Thả file để tải lên</span>
              </div>
            )}

          </div>

          <FilePreviewModal
            open={!!previewFile}
            filePath={previewFile?.filePath || ''}
            fileName={previewFile?.fileName}
            onClose={() => setPreviewFile(null)}
            zIndex={1300}
          />

          <div className="cvda-detail-form-sections-group">
            {/* Checklist */}
            <section className="cvda-detail-form-section-block">
              <div className="cvda-detail-form-panel mb-0">
                <div className="cvda-detail-form-section-header">
                  <span className="cvda-detail-form-section-title">
                    <Space size={6} align="center">
                      Check list
                      {checklistItems.length > 0 && <Badge count={checklistItems.length} showZero color="blue" size="small" />}
                    </Space>
                  </span>
                  <Button
                    type="default"
                    icon={<PlusOutlined />}
                    title="Thêm checklist"
                    onClick={() => {
                      const newItem = {
                        id: CommonJs.createTempID(),
                        ten: 'Tên Công Việc',
                        isCheck: false,
                        dateTime: dayjs().format('DD/MM/YYYY'),
                      };
                      setChecklistItems([...checklistItems, newItem]);
                      scheduleChecklistSync(newItem);
                    }}
                  >
                  </Button>
                </div>
                <div className="cvda-detail-form-section-card">
                  {checklistItems.length > 0 ? (
                    <div className="cvda-detail-form-checklist-box">
                      {checklistItems.map((item) => (
                        <div key={item.id} className="cvda-detail-form-section-row cvda-detail-form-checklist-item">
                          <Checkbox
                            checked={item.isCheck}
                            onChange={(e) => {
                              setChecklistItems((prev) => {
                                const updated = prev.map((i) =>
                                  i.id === item.id ? { ...i, isCheck: e.target.checked } : i
                                );
                                const current = updated.find((i) => i.id === item.id);
                                if (current) {
                                  scheduleChecklistSync(current);
                                }
                                return updated;
                              });
                            }}
                          />
                          <Input
                            value={item.ten}
                            placeholder="Tên"
                            className="!border-0 !bg-transparent !hover:border-0 flex-1 min-w-0 !text-[12px]"
                            style={{ textDecoration: item.isCheck ? 'line-through' : 'none' }}
                            size="small"
                            onChange={(e) => {
                              const value = e.target.value;
                              setChecklistItems((prev) => {
                                const updated = prev.map((i) =>
                                  i.id === item.id ? { ...i, ten: value } : i
                                );
                                const current = updated.find((i) => i.id === item.id);
                                if (current) {
                                  scheduleChecklistSync(current);
                                }
                                return updated;
                              });
                            }}
                          />
                          <div className="cvda-detail-form-checklist-actions">
                            <div className={`cvda-detail-form-checklist-date${item.isCheck ? ' is-done' : ''}`}>
                              <CalendarOutlined style={{ fontSize: 12, color: '#42b814' }} />
                              <span>{item.dateTime}</span>
                            </div>
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => {
                                setChecklistItems((prev) => prev.filter((i) => i.id !== item.id));
                                scheduleChecklistRemove(item.id);
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="cvda-detail-form-section-empty">Chưa có checklist</div>
                  )}
                </div>
              </div>
            </section>

            {/* Công việc con */}
            <section
              className={`cvda-detail-form-section-block${childsColumns.length === 0 && !isEdit ? ' hidden' : ''}`}
            >
              <div className="cvda-detail-form-panel mb-0">
                <div className="cvda-detail-form-section-header">
                  <span className="cvda-detail-form-section-title">
                    <Space size={6} align="center">
                      Công việc con
                      {childsColumns.length > 0 && <Badge count={childsColumns.length} showZero color="blue" size="small" />}
                    </Space>
                  </span>
                  {isEdit && (
                    <Button
                      
                      type="default"
                      icon={<PlusOutlined />}
                      title="Thêm công việc con"
                      onClick={handleAddColumn}
                      hidden
                    >
                      Thêm
                    </Button>
                  )}
                </div>
                <div className="cvda-detail-form-section-card">
                  {childsColumns.length > 0 ? (
                    <div className="cvda-detail-form-child-box">
                      <div ref={childTableScrollRef} className="custom-scrollbar overflow-x-auto overflow-y-auto max-h-[280px]">
                        <div className="min-w-[560px]">
                          {childsColumns.sort((a: any, b: any) => a.viTri - b.viTri).map((item: any, index: number) => {
                            const isNew = isNewItem(item.id);
                            const childDatePickerValue = ((): [dayjs.Dayjs | null, dayjs.Dayjs | null] => {
                              if (item.dateTime) {
                                if (item.dateTime.includes(' - ')) {
                                  const [startStr, endStr] = item.dateTime.split(' - ');
                                  const start = dayjs(startStr, 'DD/MM/YYYY');
                                  const end = dayjs(endStr, 'DD/MM/YYYY');
                                  if (start.isValid() && end.isValid()) {
                                    return [start, end];
                                  }
                                } else {
                                  const date = dayjs(item.dateTime, 'DD/MM/YYYY');
                                  if (date.isValid()) {
                                    return [date, date];
                                  }
                                }
                              }
                              if (initialRangeRef.current) {
                                return [initialRangeRef.current[0], initialRangeRef.current[1]];
                              }
                              const parentDatetime = form?.getFieldValue('datetime');
                              if (parentDatetime && Array.isArray(parentDatetime) && parentDatetime[0]) {
                                const start = dayjs(parentDatetime[0]);
                                const end = parentDatetime[1] ? dayjs(parentDatetime[1]) : start;
                                return [start, end];
                              }
                              return [null, null];
                            })();

                            return (
                              <div key={`${item.id}-${index}`} data-child-row-id={item.id} className="cvda-detail-form-section-row cvda-detail-form-child-row">
                                <div className="cvda-detail-form-child-main">
                                  <span className="shrink-0 text-[#4B5563]">{index + 1}.</span>
                                  {isNew ? (
                                    <Input
                                      value={item.projectName}
                                      placeholder="Tên công việc"
                                      className="!border-0 !bg-transparent !hover:border-0 text-[13px] flex-1 min-w-0"
                                      size="small"
                                      onChange={(e) => {
                                        const newColumns = childsColumns.map((col) =>
                                          col.id === item.id ? { ...col, projectName: e.target.value } : col
                                        );
                                        setChildsColumns(newColumns);
                                        if (form) {
                                          form.setFieldValue('childsColumns', newColumns);
                                        }
                                      }}
                                    />
                                  ) : (
                                    <span
                                      className="truncate ml-2 text-[12px] text-[#4B5563]"
                                      title={item.projectName}
                                    >
                                      {item.projectName}
                                    </span>
                                  )}
                                </div>

                                <div className="flex items-center gap-3 shrink-0">
                                  <div className="w-[350px] min-w-[150px]">
                                    {isNew ? (
                                      <EmployeeAvatarSelect
                                        name={`childsColumns_${item.id}`}
                                        mode="single"
                                        isColumn={isColumn}
                                        width={150}
                                        maxWidth={150}
                                        form={form}
                                        showName
                                        onChange={(value) => {
                                          const parentNv =
                                            form?.getFieldValue('nv') ?? data?.duAnCongViec?.congViec?.nguoiPhuTrachID;
                                          const personId =
                                            (value as string) ||
                                            firstPhuTrachIdForChildDefault(parentNv) ||
                                            '';
                                          const newColumns = childsColumns.map((col) =>
                                            col.id === item.id ? { ...col, person: personId } : col
                                          );
                                          setChildsColumns(newColumns);
                                          if (form) {
                                            form.setFieldValue('childsColumns', newColumns);
                                            form.setFieldValue(`childsColumns_${item.id}`, personId);
                                          }
                                        }}
                                      />
                                    ) : (
                                      <div className="flex items-center">
                                        <EmployeeAvatar
                                          employeeInfo={buildChildEmployeeInfo(item.person)}
                                          size={24}
                                        />
                                      </div>
                                    )}
                                  </div>

                                  <div style={{ minWidth: 100, fontSize: 13, display: 'flex', alignItems: 'center' }}>
                                    {isNew ? (
                                      <ActionPlanDateTimePicker
                                        value={childDatePickerValue}
                                        onChange={(dates: any) => {
                                          const dateTimeStr =
                                            dates && dates[0] && dates[1]
                                              ? `${dates[0].format('DD/MM/YYYY')} - ${dates[1].format('DD/MM/YYYY')}`
                                              : '';
                                          const newColumns = childsColumns.map((col) =>
                                            col.id === item.id ? { ...col, dateTime: dateTimeStr } : col
                                          );
                                          setChildsColumns(newColumns);
                                          if (form) {
                                            form.setFieldValue('childsColumns', newColumns);
                                          }
                                        }}
                                        initialRange={currentInitialRange}
                                        style={{ border: 'none', boxShadow: 'none', fontSize: 13, marginLeft: -8 }}
                                      />
                                    ) : (
                                      <>
                                        <CalendarOutlined style={{ color: '#42b814', marginRight: 4, fontSize: 13 }} />
                                        <span style={{ fontSize: 12, color: '#6b7280' }}>{item.originalData?.thoiGian || '-'}</span>
                                      </>
                                    )}
                                  </div>

                                  <div hidden={!isNew} style={{ width: 32, display: 'flex', justifyContent: 'center' }}>
                                    <Button
                                      type="text"
                                      size="small"
                                      danger
                                      icon={<DeleteOutlined />}
                                      onClick={() => handleDeleteColumn(item.id)}
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="cvda-detail-form-section-empty">Chưa có công việc con</div>
                  )}
                </div>
              </div>
            </section>

            {!hideDiscussion ? (
              <section className="cvda-detail-form-section-block">{thaoLuanDiscussList}</section>
            ) : null}
          </div>
        </div>
        {!hideDiscussion ? (
          <div
            className={`cvda-detail-form-discuss-dock${formBodyScrolled && !formBodyAtBottom ? ' has-scroll-shadow' : ''}`}
          >
            {thaoLuanComposer}
          </div>
        ) : null}
        {/* Chia chỉ tiêu Modal */}
        {parentCongViec && (
          <ChiaChiTieuModal
            visible={chiaChiTieuModalVisible}
            onCancel={() => {
              setChiaChiTieuModalVisible(false);
            }}
            persistViaParentSave
            readOnly={!isEdit}
            onSaveChiaChiTieuSuccess={(payload: any) => {
              if (payload?.listChiTieu?.length) {
                setListChiTieuDraft((prev) => mergeListChiTieuItems(prev, payload.listChiTieu));
              }
              if (payload?.chiTieu !== undefined) setChiTieuValue(Number(payload.chiTieu));
              if (payload?.hinhThuc !== undefined) setHinhThucChiaChiTieu(Number(payload.hinhThuc));
            }}
            record={{
              congViec: {
                ...parentCongViec,
                chiTieu: chiTieuValue,
                hinhThuc: hinhThucChiaChiTieu,
                IDDoLuong: idDoLuongValue,
                idDoLuong: idDoLuongValue,
                tanSuat: tanSuatValue,
              },
              thoiGian: '',
            }}
            listChiTieu={listChiTieuDraft}
          />
        )}
        {!hideDiscussion ? thaoLuanMentionPortal : null}
      </div >
    </>
  );
});

FormInfomation.displayName = 'FormInfomation';
